"use client"

import { useState } from "react"
import { motion, Variants } from "framer-motion"
import Image from "next/image"
import Header from "@/components/header"
import Footer from "@/components/footer"
import {
    Coins,
    Globe,
    Layers,
    Vote,
    Shield,
    TrendingUp,
    Lock,
    Rocket,
    Code2,
    FileCode,
    Zap,
    BarChart3,
    Users,
    ArrowRight,
    Copy,
    Check,
    ExternalLink,
    CircleDollarSign,
    Search,
    Megaphone,
    Building2,
    ChevronRight,
    BookOpen,
    Download,
    Server,
    Network,
    Cpu,
    MessageSquareLock,
    Heart,
    Gift,
    LucideIcon,
} from "lucide-react"

/* ─────────────────────── ANIMATION VARIANTS ─────────────────────── */

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}

const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
}

/* ─────────────────────── DATA ─────────────────────── */

const tokenInfo = {
    name: "TokenKickstarter",
    symbol: "TKS",
    decimals: 18,
    totalSupply: "1B / network",
    networks: ["BSC", "Ethereum"],
    upcomingNetworks: ["Polygon", "Base"],
}

const tokenomics = [
    { label: "Presale", pct: 40, color: "bg-emerald-500" },
    { label: "Staking Rewards", pct: 20, color: "bg-blue-500" },
    { label: "Ecosystem Fund", pct: 15, color: "bg-violet-500" },
    { label: "Team & Advisors", pct: 10, color: "bg-amber-500" },
    { label: "Marketing", pct: 10, color: "bg-pink-500" },
    { label: "Liquidity Pool", pct: 5, color: "bg-cyan-500" },
]

const stakingTiers = [
    { duration: "30 Days", apy: "8%", min: "1,000 TKS" },
    { duration: "90 Days", apy: "15%", min: "5,000 TKS" },
    { duration: "180 Days", apy: "25%", min: "10,000 TKS" },
    { duration: "365 Days", apy: "40%", min: "25,000 TKS" },
]

const contracts = {
    mainnet: {
        token: [
            { network: "BSC", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4", explorer: "https://etherscan.io" },
        ],
        vault: [
            { network: "BSC", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1", explorer: "https://etherscan.io" },
        ],
        collector: [
            { network: "BSC", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://basescan.org" },
        ],
        staking: [
            { network: "BSC", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501", explorer: "https://etherscan.io" },
        ],
        presaleFactory: [
            { network: "BSC", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://basescan.org" },
        ],
        tokenLocker: [
            { network: "BSC", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://basescan.org" },
        ],
        deployerProxy: [
            { network: "BSC", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://basescan.org" },
        ],
        fairLaunchFactory: [
            { network: "BSC", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://basescan.org" },
        ],
        collectorFactory: [
            { network: "BSC", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://bscscan.com" },
            { network: "Ethereum", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://etherscan.io" },
            { network: "Polygon", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://polygonscan.com" },
            { network: "Base", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://basescan.org" },
        ],
    },
    testnet: {
        token: [
            { network: "BSC Testnet", address: "0x8664E4345E2181032F1dDfbCCE210456C0eD8401", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x8664E4345E2181032F1dDfbCCE210456C0eD8401", explorer: "https://sepolia.etherscan.io" },
        ],
        vault: [
            { network: "BSC Testnet", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd", explorer: "https://sepolia.etherscan.io" },
        ],
        collector: [
            { network: "BSC Testnet", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee", explorer: "https://sepolia.basescan.org" },
        ],
        staking: [
            { network: "BSC Testnet", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366", explorer: "https://sepolia.etherscan.io" },
        ],
        presaleFactory: [
            { network: "BSC Testnet", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D", explorer: "https://sepolia.basescan.org" },
        ],
        tokenLocker: [
            { network: "BSC Testnet", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132", explorer: "https://sepolia.basescan.org" },
        ],
        deployerProxy: [
            { network: "BSC Testnet", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12", explorer: "https://sepolia.basescan.org" },
        ],
        fairLaunchFactory: [
            { network: "BSC Testnet", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5", explorer: "https://sepolia.basescan.org" },
        ],
        collectorFactory: [
            { network: "BSC Testnet", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://testnet.bscscan.com" },
            { network: "Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://sepolia.etherscan.io" },
            { network: "Polygon Amoy", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://amoy.polygonscan.com" },
            { network: "Base Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657", explorer: "https://sepolia.basescan.org" },
        ],
    },
}

/* ─────────────────────── TABLE OF CONTENTS ─────────────────────── */

const tocItems = [
    { id: "abstract", label: "Abstract", icon: BookOpen },
    { id: "token-details", label: "Token Details", icon: Coins },
    { id: "tokenomics", label: "Tokenomics", icon: BarChart3 },
    { id: "staking", label: "Staking", icon: TrendingUp },
    { id: "create-presale", label: "Create Presale", icon: Rocket },
    { id: "create-token", label: "Create Token", icon: Code2 },
    { id: "token-locking", label: "Token Locking", icon: Lock },
    { id: "listing-directory", label: "Listing Directory", icon: Search },
    { id: "smart-contracts", label: "Smart Contracts", icon: FileCode },
    { id: "technical-deep-dive", label: "Technical Deep Dive", icon: Code2 },
    { id: "architecture", label: "Architecture", icon: Layers },
    { id: "revenue", label: "Revenue & Ecosystem", icon: CircleDollarSign },
    { id: "donation-campaigns", label: "Donation Campaigns", icon: Heart },
    { id: "airdrop-quests", label: "Airdrop Quests", icon: Gift },
    { id: "tks-blockchain", label: "TKS Layer 1 Blockchain", icon: Server },
    { id: "investor-benefits", label: "Investor Benefits", icon: TrendingUp },
    { id: "market-analysis", label: "Market Analysis", icon: Building2 },
    { id: "risk-disclosures", label: "Risk Disclosures", icon: Shield },
    { id: "references", label: "References", icon: BookOpen },
]

/* ─────────────────────── REUSABLE SUB-COMPONENTS ─────────────────────── */

function SectionHeading({ id, icon: Icon, badge, title, titleHighlight, description }: {
    id: string
    icon: LucideIcon
    badge: string
    title: string
    titleHighlight: string
    description: string
}) {
    return (
        <motion.div
            id={id}
            className="mb-12 text-center scroll-mt-28"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
        >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                <Icon className="h-4 w-4" />
                {badge}
            </div>
            <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
                {title}{" "}
                <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                    {titleHighlight}
                </span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{description}</p>
        </motion.div>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold">{value}</span>
        </div>
    )
}

function AddressRow({ network, address, explorer }: { network: string; address: string; explorer: string }) {
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        navigator.clipboard.writeText(address)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    const short = `${address.slice(0, 6)}...${address.slice(-4)}`

    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 group hover:bg-muted/40 transition-all">
            <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap min-w-[80px]">{network}</span>
                <code className="text-sm font-mono truncate hidden sm:block">{address}</code>
                <code className="text-sm font-mono sm:hidden">{short}</code>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={handleCopy} className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all" title="Copy address">
                    {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <a href={`${explorer}/address/${address}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all" title="View on explorer">
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            </div>
        </div>
    )
}

function FeatureCard({ icon: Icon, title, description }: { icon: LucideIcon; title: string; description: string }) {
    return (
        <motion.div variants={fadeUp} className="glass-card p-6 group hover:border-primary/40 transition-all">
            <div className="mb-4 h-12 w-12 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:from-primary/30 group-hover:to-primary/10 transition-all group-hover:scale-110">
                <Icon className="h-6 w-6 text-primary" />
            </div>
            <h4 className="font-bold mb-2">{title}</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </motion.div>
    )
}

/* ─────────────────────── MAIN PAGE ─────────────────────── */

export function WhitepaperContent() {
    return (
        <div className="min-h-screen flex flex-col relative">
            <Header />
            <main className="flex-1 relative z-10">
                <article itemScope itemType="https://schema.org/TechArticle" className="whitepaper-article">
                <meta itemProp="version" content="2.2" />
                <meta itemProp="datePublished" content="2026-04-25" />
                <meta itemProp="dateModified" content="2026-09-18" />
                <meta itemProp="author" content="TokenKickstarter" />
                <meta itemProp="headline" content="TokenKickstarter (TKS) — Official Technical Whitepaper v2.2" />
                <meta itemProp="description" content="Factory-pattern smart contract architecture for token deployment, presale management, and liquidity infrastructure across EVM-compatible chains." />
                <meta itemProp="wordCount" content="35000" />
                <meta itemProp="inLanguage" content="en-US" />

                {/* Force all content visible for crawlers/scanners that don't execute JS */}
                <noscript>
                    <style>{`
                        .whitepaper-article * {
                            opacity: 1 !important;
                            transform: none !important;
                            visibility: visible !important;
                        }
                    `}</style>
                </noscript>

                {/* SSR visibility override — ensures search engines and token scanners see all content */}
                <style>{`
                    @media print {
                        .whitepaper-article * {
                            opacity: 1 !important;
                            transform: none !important;
                        }
                    }
                `}</style>
                <section className="pt-32 pb-16 md:pt-40 md:pb-20 relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    <div className="container relative z-10 text-center">
                        <motion.div
                            className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <BookOpen className="h-4 w-4" />
                            Official Whitepaper
                        </motion.div>
                        <motion.h1
                            className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                        >
                            Token
                            <span className="bg-gradient-to-r from-primary via-primary to-primary/70 bg-clip-text text-transparent">
                                Kickstarter
                            </span>
                        </motion.h1>
                        <motion.p
                            className="mx-auto max-w-3xl text-lg sm:text-xl text-muted-foreground leading-relaxed"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                        >
                            A factory-pattern smart contract architecture for token deployment, presale management,
                            and liquidity infrastructure across EVM-compatible chains. Version 2.2 — Sep 2026.
                        </motion.p>

                        {/* Table of Contents */}
                        <motion.div
                            className="mt-12 glass-card p-6 sm:p-8 max-w-2xl mx-auto text-left"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                <Layers className="h-5 w-5 text-primary" />
                                Table of Contents
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {tocItems.map((item, i) => {
                                    const TocIcon = item.icon
                                    return (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/40 transition-all group text-sm"
                                        >
                                            <span className="text-xs text-muted-foreground w-5 text-right shrink-0">
                                                {i + 1}.
                                            </span>
                                            <TocIcon className="h-4 w-4 text-primary shrink-0" />
                                            <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                                                {item.label}
                                            </span>
                                            <ChevronRight className="h-3 w-3 ml-auto text-muted-foreground/50 group-hover:text-primary transition-colors" />
                                        </a>
                                    )
                                })}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 1. ABSTRACT ═══ */}
                <section className="py-16 md:py-20" id="abstract">
                    <div className="container">
                        <SectionHeading
                            id=""
                            icon={BookOpen}
                            badge="Section 1"
                            title=""
                            titleHighlight="Abstract"
                            description=""
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                                    TokenKickstarter (TKS) introduces a <strong className="text-foreground">factory-pattern smart contract architecture</strong> that
                                    separates token deployment, presale management, and liquidity locking into independently upgradeable
                                    modules. Unlike monolithic launchpad contracts used by competing platforms, each TKS factory
                                    (TokenDeployerProxy, PresaleFactory, FairLaunchPresaleFactory, ContributionCollectorFactory) operates
                                    as an autonomous deployer — creating individual, isolated smart contracts per user project. This
                                    isolation model ensures that a vulnerability in one deployed token cannot cascade to other projects
                                    on the platform. Currently live on Ethereum Mainnet and BNB Chain, with Base and Polygon deployments
                                    active for presale collection, the platform processes token deployments for a flat $20 USD fee
                                    converted to native currency via Chainlink AggregatorV3Interface oracle price feeds at the time
                                    of transaction <sup>[7]</sup>.
                                </p>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-4">
                                    The TKS ecosystem addresses a specific structural inefficiency in the current launchpad market:
                                    the misalignment between platform revenue and user success. Traditional launchpads extract 2–5%
                                    from presale raise amounts, creating an incentive to approve low-quality projects that maximize
                                    volume. TokenKickstarter&apos;s fee model charges fixed USD-denominated fees (e.g., $20 for token
                                    creation, $150 for presale factory deployment) independent of raise size, ensuring the platform
                                    profits from tooling quality rather than fundraising volume. All fees are converted from USD to
                                    native tokens using Chainlink&apos;s <code className="text-xs bg-muted px-1 py-0.5 rounded">latestRoundData()</code> with
                                    a 1-hour staleness threshold and configurable fallback pricing <sup>[7][9]</sup>.
                                </p>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-4">
                                    The TKS token serves as the <strong className="text-foreground">economic coordination layer</strong> across
                                    all platform modules. Rather than implementing a simple discount-for-holding model, TKS integrates
                                    at the smart contract level: the TKSStakingMaster contract supports four distinct pool types
                                    (TKS, LP tokens, stablecoins, and native BNB) with duration-based APY tiers ranging from 8%
                                    (30-day lock) to 40% (365-day lock). The TKSClaimVault employs ECDSA signature verification
                                    with chain-ID-scoped nonces for cross-chain presale claim resolution — enabling contributors
                                    on Polygon or Base to claim tokens minted on BSC without requiring a third-party bridge
                                    protocol <sup>[5][6]</sup>. Revenue from platform operations and subsidiary ventures funds
                                    periodic buy-back events executed through the platform&apos;s contribution collector infrastructure.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 2. TOKEN DETAILS ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="token-details"
                            icon={Coins}
                            badge="Section 2"
                            title="Token"
                            titleHighlight="Details"
                            description="TKS is the native utility token powering the TokenKickstarter ecosystem."
                        />
                        <motion.div
                            className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Coins className="h-5 w-5 text-primary" />
                                    Token Information
                                </h3>
                                <div className="space-y-3">
                                    <InfoRow label="Token Name" value={tokenInfo.name} />
                                    <InfoRow label="Symbol" value={`$${tokenInfo.symbol}`} />
                                    <div className="flex justify-between items-center p-4 rounded-xl bg-muted/30">
                                        <span className="text-muted-foreground">Official Logo</span>
                                        <div className="h-12 w-12 rounded-full overflow-hidden shadow-md shadow-primary/10">
                                            <Image src="/token_logo.png" alt="TKS Token" width={48} height={48} className="w-full h-full object-cover" unoptimized />
                                        </div>
                                    </div>
                                    <InfoRow label="Decimals" value={String(tokenInfo.decimals)} />
                                    <InfoRow label="Total Supply" value={`${tokenInfo.totalSupply} TKS`} />
                                    <div className="p-4 rounded-xl bg-muted/30">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-muted-foreground flex items-center gap-2">
                                                <Globe className="h-4 w-4" />
                                                Deployed Networks
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {tokenInfo.networks.map((n) => (
                                                <span key={n} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                                    {n}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="flex justify-between items-center mb-2 mt-4">
                                            <span className="text-muted-foreground text-sm">Upcoming</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {tokenInfo.upcomingNetworks.map((n) => (
                                                <span key={n} className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">
                                                    {n}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Layers className="h-5 w-5 text-primary" />
                                    Token Utility
                                </h3>
                                <div className="space-y-4">
                                    {[
                                        { icon: Coins, title: "Platform Fees", desc: "Pay reduced fees when using TKS for token creation, presales, and locking" },
                                        { icon: TrendingUp, title: "Staking Rewards", desc: "Stake TKS to earn passive income with duration-based APY" },
                                        { icon: Vote, title: "Governance", desc: "Vote on platform proposals and shape the future of TokenKickstarter" },
                                        { icon: Shield, title: "Priority Access", desc: "Get early access to new presales and exclusive platform features" },
                                    ].map((u) => {
                                        const UIcon = u.icon
                                        return (
                                            <div key={u.title} className="flex gap-4 p-4 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                                <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-all">
                                                    <UIcon className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold mb-1">{u.title}</h4>
                                                    <p className="text-sm text-muted-foreground">{u.desc}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 3. TOKENOMICS ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="tokenomics"
                            icon={BarChart3}
                            badge="Section 3"
                            title="Token"
                            titleHighlight="Distribution"
                            description="1 billion TKS tokens strategically allocated to ensure long-term sustainability."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-3xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-5">
                                {tokenomics.map((t) => (
                                    <div key={t.label}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="font-medium">{t.label}</span>
                                            <span className="text-primary font-bold">{t.pct}%</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-muted/40 overflow-hidden">
                                            <motion.div
                                                className={`h-full rounded-full ${t.color}`}
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${t.pct}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-border/30 grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                                <div className="p-4 rounded-xl bg-muted/30">
                                    <div className="text-2xl font-bold text-primary">1B</div>
                                    <div className="text-xs text-muted-foreground mt-1">Total Supply</div>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/30">
                                    <div className="text-2xl font-bold text-primary">18</div>
                                    <div className="text-xs text-muted-foreground mt-1">Decimals</div>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/30 col-span-2 sm:col-span-1">
                                    <div className="text-2xl font-bold text-primary">ERC-20</div>
                                    <div className="text-xs text-muted-foreground mt-1">Standard</div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 4. STAKING ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="staking"
                            icon={TrendingUp}
                            badge="Section 4"
                            title="Staking"
                            titleHighlight="Program"
                            description="Earn passive income by staking TKS with duration-based reward tiers."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {stakingTiers.map((tier) => (
                                <motion.div key={tier.duration} variants={fadeUp} className="glass-card p-6 text-center group hover:border-primary/40 transition-all">
                                    <div className="text-3xl font-bold text-primary mb-1">{tier.apy}</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-4">APY</div>
                                    <div className="h-px bg-border/30 mb-4" />
                                    <div className="font-semibold mb-1">{tier.duration}</div>
                                    <div className="text-sm text-muted-foreground">Min: {tier.min}</div>
                                </motion.div>
                            ))}
                        </motion.div>

                        <motion.div
                            className="glass-card p-8 mt-8 max-w-5xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="font-bold text-lg mb-4">Staking Features</h3>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {[
                                    "Stake TKS, LP tokens, USDT, or native BNB in separate pool contracts",
                                    "APY tiers enforced on-chain: 8% (30d), 15% (90d), 25% (180d), 40% (365d)",
                                    "TKS reward token set as immutable in TKSStakingMaster constructor",
                                    "Emergency withdraw function forfeits pending rewards but returns principal",
                                    "Configurable deposit/withdrawal fees per pool (set by contract owner)",
                                    "Recovery function explicitly blocks withdrawal of staking and reward tokens",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                        <Zap className="h-4 w-4 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 5. CREATE PRESALE ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="create-presale"
                            icon={Rocket}
                            badge="Section 5"
                            title="Create"
                            titleHighlight="Presale"
                            description="Launch your own token presale with smart-contract powered fundraising."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <FeatureCard icon={TrendingUp} title="Oracle-Powered USD Pricing" description="Every contribution is converted at the exact transaction moment via Chainlink AggregatorV3Interface. The TKSContributionCollector queries latestRoundData() with a 1-hour staleness threshold, falling back to cached pricing if the oracle is temporarily unavailable [7][12]." />
                            <FeatureCard icon={Globe} title="6-Currency Acceptance" description="The collector contract routes native gas tokens (ETH, BNB, MATIC) through Chainlink price feeds while stablecoins (USDT, USDC, DAI) are accepted at face value with IERC20Metadata decimal normalization — supporting both 6-decimal and 18-decimal tokens." />
                            <FeatureCard icon={Users} title="On-Chain Referral Tracking" description="Referral addresses are recorded immutably in the ContributionCollector's referral mapping. The default 5% rate (configurable up to 20% by presale creator) is calculated and distributed at the contract level, not through off-chain tracking." />
                            <FeatureCard icon={Shield} title="Dual-Cap Architecture" description="Each presale deploys with independently configurable soft cap (minimum viable raise) and hard cap (maximum). If only the soft cap is met, contributors claim tokens at the defined rate. Below soft cap, full refunds execute through the claim/refund mechanism." />
                            <FeatureCard icon={Lock} title="51% Minimum Liquidity Rule" description="The PresaleFactory enforces a minimum 51% liquidity allocation on finalization. This percentage of raised funds is automatically paired with presale tokens and locked in the TokenLockerUpgradeable contract for the creator-specified duration [9]." />
                            <FeatureCard icon={Zap} title="Non-Custodial Treasury" description="Unlike platforms that escrow funds until manual release, TKS contributions transfer to the project's receiver address within the same transaction via Solidity's call{value:} pattern. The platform never holds user funds in an intermediary contract." />
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 6. CREATE TOKEN ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="create-token"
                            icon={Code2}
                            badge="Section 6"
                            title="Create"
                            titleHighlight="Token"
                            description="No-code ERC-20 token factory with advanced features built in."
                        />
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <FeatureCard icon={Coins} title="Bytecode-Level Deployment" description="The TokenDeployerProxy receives pre-compiled Solidity bytecode from the frontend and deploys using the EVM CREATE opcode. This enables fully customizable token logic including supply (up to 10^27), name, symbol, and 0-18 decimal places — all configured through a no-code interface." />
                            <FeatureCard icon={Shield} title="Configurable Whale Limits" description="Each deployed token includes adjustable maxTransactionAmount (default: 1M tokens) and maxWalletBalance (default: 2M tokens) enforced at the transfer() level. A 30-second cooldown (configurable up to 10 minutes) between transactions prevents sandwich attacks on low-liquidity pairs." />
                            <FeatureCard icon={TrendingUp} title="Three-Way Tax Split" description="Buy, sell, and transfer taxes are independently configurable from 0-25% (hard-capped in contract). Tax revenue splits across three destinations: marketing wallet, development fund, and auto-liquidity generation — with ratios adjustable by the token owner." />
                            <FeatureCard icon={Zap} title="Auto-LP via Tax" description="When accumulated tax tokens reach the swap threshold, the contract automatically sells half for the paired asset and adds both to the liquidity pool via the DEX router. This creates continuous buy pressure and deepening liquidity without manual intervention." />
                            <FeatureCard icon={Lock} title="Ownable2Step Transfer" description="Token contracts inherit OpenZeppelin's Ownable2StepUpgradeable [3] — requiring the new owner to explicitly accept ownership, preventing accidental transfers to wrong addresses. Ownership can be permanently renounced, making the contract fully immutable." />
                            <FeatureCard icon={FileCode} title="Source Auto-Verification" description="Post-deployment, the platform programmatically submits source code and constructor arguments to BscScan, Etherscan, PolygonScan, and BaseScan APIs. Every TKS-created token is verified within 60 seconds of deployment, enabling immediate public audit of the contract code." />
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 7. TOKEN LOCKING ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="token-locking"
                            icon={Lock}
                            badge="Section 7"
                            title="Token"
                            titleHighlight="Locking"
                            description="Secure tokens and LP with time-locked or vesting schedules to build investor trust."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="grid gap-6 md:grid-cols-2">
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg flex items-center gap-2">
                                        <Lock className="h-5 w-5 text-primary" />
                                        Time Lock
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        The TokenLockerUpgradeable contract (deployed at
                                        <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132</code>
                                        across all chains) accepts any ERC-20 token including LP tokens.
                                        Standard locks hold the full token amount until the specified UNIX
                                        timestamp, at which point the lock owner can call withdraw(). Currently
                                        offered at zero platform fee [9].
                                    </p>
                                    <div className="space-y-2">
                                        {["Customizable lock duration", "Auto-release on expiry", "On-chain proof of lock", "Multi-token support"].map((f, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                        Vesting Schedule
                                    </h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Vesting locks use a linear release formula: claimable = totalAmount *
                                        (currentTime - cliffEnd) / (vestEnd - cliffEnd). The cliff period
                                        prevents any withdrawal until the specified date; after the cliff,
                                        tokens unlock proportionally every block. Lock ownership is transferable
                                        via a dedicated transferLockOwnership() function.
                                    </p>
                                    <div className="space-y-2">
                                        {["Cliff + linear vesting", "Milestone-based release", "Revocable or irrevocable", "Transparent on-chain schedule"].map((f, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{f}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 8. LISTING DIRECTORY ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="listing-directory"
                            icon={Search}
                            badge="Section 8 — Upcoming"
                            title="Token Listing"
                            titleHighlight="Directory"
                            description="A comprehensive directory for discovering tokens and presales across all supported chains."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="flex items-start gap-4 mb-6">
                                <div className="h-12 w-12 shrink-0 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                                    <Search className="h-6 w-6 text-amber-500" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="font-bold text-lg">Coming Soon</h3>
                                        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold">UPCOMING</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        The Token Listing Directory will be a discovery hub for all tokens created
                                        and presales launched through TokenKickstarter. Users will be able to search,
                                        filter, and explore projects with detailed analytics, social metrics, and
                                        audit status.
                                    </p>
                                </div>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {[
                                    "Search & filter by chain, category, and status",
                                    "Real-time price and volume data",
                                    "KYC & audit verification badges",
                                    "Community ratings and reviews",
                                    "Trending and featured listings",
                                    "Integration with DEX aggregators",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm p-3 rounded-lg bg-muted/30">
                                        <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 9. SMART CONTRACTS ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="smart-contracts"
                            icon={FileCode}
                            badge="Section 9"
                            title="Smart"
                            titleHighlight="Contracts"
                            description="All deployed contract addresses across mainnet and testnet networks."
                        />
                        <motion.div
                            className="glass-card p-8 max-w-5xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            {/* ─── Mainnet ─── */}
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-5">
                                    <span className="px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-semibold uppercase tracking-wider">
                                        Mainnet
                                    </span>
                                </div>
                                <div className="grid gap-6 lg:grid-cols-2">
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                        <div className="space-y-1.5">{contracts.mainnet.token.map((c) => <AddressRow key={`mt-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                        <div className="space-y-1.5">{contracts.mainnet.vault.map((c) => <AddressRow key={`mv-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Contribution Collector</p>
                                        <div className="space-y-1.5">{contracts.mainnet.collector.map((c) => <AddressRow key={`mc-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Staking Contract</p>
                                        <div className="space-y-1.5">{contracts.mainnet.staking.map((c) => <AddressRow key={`ms-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Presale Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.presaleFactory.map((c) => <AddressRow key={`mpf-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Locker</p>
                                        <div className="space-y-1.5">{contracts.mainnet.tokenLocker.map((c) => <AddressRow key={`ml-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Deployer Proxy</p>
                                        <div className="space-y-1.5">{contracts.mainnet.deployerProxy.map((c) => <AddressRow key={`mdp-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Fair Launch Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.fairLaunchFactory.map((c) => <AddressRow key={`mfl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Collector Factory</p>
                                        <div className="space-y-1.5">{contracts.mainnet.collectorFactory.map((c) => <AddressRow key={`mcf-${c.network}`} {...c} />)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* ─── Testnet ─── */}
                            <div>
                                <div className="flex items-center gap-2 mb-5">
                                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-semibold uppercase tracking-wider">
                                        Testnet
                                    </span>
                                </div>
                                <div className="grid gap-6 lg:grid-cols-2">
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">TKS Token</p>
                                        <div className="space-y-1.5">{contracts.testnet.token.map((c) => <AddressRow key={`tt-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Claim Vault <span className="text-xs opacity-60">(UUPS Proxy)</span></p>
                                        <div className="space-y-1.5">{contracts.testnet.vault.map((c) => <AddressRow key={`tv-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Contribution Collector</p>
                                        <div className="space-y-1.5">{contracts.testnet.collector.map((c) => <AddressRow key={`tc-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Staking Contract</p>
                                        <div className="space-y-1.5">{contracts.testnet.staking.map((c) => <AddressRow key={`ts-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Presale Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.presaleFactory.map((c) => <AddressRow key={`tpf-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Locker</p>
                                        <div className="space-y-1.5">{contracts.testnet.tokenLocker.map((c) => <AddressRow key={`tl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Token Deployer Proxy</p>
                                        <div className="space-y-1.5">{contracts.testnet.deployerProxy.map((c) => <AddressRow key={`tdp-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Fair Launch Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.fairLaunchFactory.map((c) => <AddressRow key={`tfl-${c.network}`} {...c} />)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-muted-foreground mb-2">Collector Factory</p>
                                        <div className="space-y-1.5">{contracts.testnet.collectorFactory.map((c) => <AddressRow key={`tcf-${c.network}`} {...c} />)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Contract Architecture */}
                            <div className="mt-8 pt-6 border-t border-border/30">
                                <h3 className="font-bold text-lg mb-4">Contract Architecture</h3>
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">OpenZeppelin</div>
                                        <div className="text-xs text-muted-foreground">Battle-Tested Libraries</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">UUPS</div>
                                        <div className="text-xs text-muted-foreground">Upgradeable Proxy Pattern</div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/30 text-center">
                                        <div className="text-xl font-bold text-primary mb-1">Chainlink</div>
                                        <div className="text-xs text-muted-foreground">Oracle Price Feeds</div>
                                    </div>
                                </div>
                            </div>

                            {/* Contract Technical Details */}
                            <div className="mt-8 pt-6 border-t border-border/30">
                                <h3 className="font-bold text-lg mb-2">Contract Technical Details</h3>
                                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                                    Detailed technical breakdown of every deployed smart contract — inheritance, libraries, security features, and access control.
                                </p>
                                <div className="space-y-6">

                                    {/* TKS Token */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Coins className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TokenKickstarter (TKS Token)</h4>
                                                <span className="text-xs text-muted-foreground">_TKS_Temp.sol — UUPS Upgradeable ERC-20</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Standard:</span> ERC-20 with EIP-2612 Permit</div>
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">Upgrade Pattern:</span> UUPS Proxy (OpenZeppelin UUPSUpgradeable)</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Initializable</div>
                                                <div>• UUPSUpgradeable</div>
                                                <div>• Ownable2StepUpgradeable (2-step ownership transfer)</div>
                                                <div>• ReentrancyGuardUpgradeable</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Security Features:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Reentrancy protection on all state-changing functions</div>
                                                <div>• 2-step ownership transfer prevents accidental transfers</div>
                                                <div>• Anti-whale: max transaction (1M TKS) and max wallet (2M TKS) limits</div>
                                                <div>• Trade cooldown (30s default, max 10 min)</div>
                                                <div>• Blacklist capability for bad actors (cannot blacklist owner, pair, or router)</div>
                                                <div>• Pausable transfers (owner only)</div>
                                                <div>• Tax hard cap at 25% maximum</div>
                                                <div>• Early sell penalty (configurable, max 7 days / 25%)</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Token Features:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Reflection rewards to all holders</div>
                                                <div>• Configurable buy (3%) / sell (3%) / transfer (0%) taxes</div>
                                                <div>• Mintable (owner only)</div>
                                                <div>• Burnable (any holder)</div>
                                                <div>• Vesting support for team tokens</div>
                                                <div>• Staking pool authorization</div>
                                                <div>• Batch airdrop support</div>
                                                <div>• EIP-2612 gasless approvals</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Access Control:</span> Ownable2Step — only owner can modify taxes, features, and settings</div>
                                        </div>
                                    </div>

                                    {/* TKSClaimVault */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Lock className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TKSClaimVault</h4>
                                                <span className="text-xs text-muted-foreground">TKSClaimVault.sol — UUPS Upgradeable Cross-Chain Claim System</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.33</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> Initializable, OwnableUpgradeable, UUPSUpgradeable, ReentrancyGuardUpgradeable, SafeERC20, ECDSA, MessageHashUtils</div>
                                            <div><span className="text-foreground font-medium">Purpose:</span> Cross-chain claim vault for TKS presale. Users contribute on any chain and claim tokens from this vault</div>
                                            <div><span className="text-foreground font-medium">Security:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• ECDSA signature-based claims (prevents unauthorized claiming)</div>
                                                <div>• Nonce tracking prevents replay attacks</div>
                                                <div>• Chain ID included in signature to prevent cross-chain replay</div>
                                                <div>• ReentrancyGuard on all claim functions</div>
                                                <div>• SafeERC20 for safe token transfers</div>
                                                <div>• Authorized provider system for automated presale integration</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Access:</span> Owner sets claimable amounts and authorized providers; users claim via signatures</div>
                                        </div>
                                    </div>

                                    {/* TKSContributionCollector */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <TrendingUp className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TKSContributionCollector</h4>
                                                <span className="text-xs text-muted-foreground">TKSContributionCollector.sol — UUPS Upgradeable Presale Collector</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.33</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> Initializable, OwnableUpgradeable, UUPSUpgradeable, ReentrancyGuardUpgradeable, SafeERC20, IERC20Metadata</div>
                                            <div><span className="text-foreground font-medium">Chainlink Integration:</span> AggregatorV3Interface for real-time native/USD price conversion (BNB/USD, ETH/USD, MATIC/USD)</div>
                                            <div><span className="text-foreground font-medium">Features:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Multi-currency contributions (native tokens + stablecoins)</div>
                                                <div>• Real-time USD conversion via Chainlink oracles</div>
                                                <div>• Fixed TKS rate: $1 = 10 TKS (configurable)</div>
                                                <div>• Referral system (5% default, max 20%)</div>
                                                <div>• Auto-sync with TKSClaimVault for automated distribution</div>
                                                <div>• Instant fund transfer to receiver (no locked funds)</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Security:</span> NonReentrant on all contribution functions, SafeERC20 for token transfers, input validation on all parameters</div>
                                        </div>
                                    </div>

                                    {/* TKSStakingMaster */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <TrendingUp className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TKSStakingMaster</h4>
                                                <span className="text-xs text-muted-foreground">TKSStakingMaster.sol — Multi-Pool Staking Contract</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> IERC20, SafeERC20, ReentrancyGuard, Ownable</div>
                                            <div><span className="text-foreground font-medium">Pool Types:</span> TKS, LP Tokens, USDT, and native BNB staking</div>
                                            <div><span className="text-foreground font-medium">Features:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Duration-based APY tiers (30/90/180/365 days)</div>
                                                <div>• Configurable deposit/withdrawal fees per pool</div>
                                                <div>• TKS token as immutable reward token</div>
                                                <div>• Emergency withdraw (forfeits rewards)</div>
                                                <div>• Recovery function (cannot recover staking/reward tokens)</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Security:</span> ReentrancyGuard, SafeERC20, immutable reward token, lock period enforcement, protected recovery function</div>
                                        </div>
                                    </div>

                                    {/* TokenDeployerProxy */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Code2 className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TokenDeployerProxy</h4>
                                                <span className="text-xs text-muted-foreground">TokenDeployerProxy.sol — No-Code Token Factory</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">Inherits:</span> ChainlinkFeeManager (OpenZeppelin Ownable)</div>
                                            <div><span className="text-foreground font-medium">Chainlink Integration:</span> AggregatorV3Interface for USD→native fee conversion with staleness checks and fallback pricing</div>
                                            <div><span className="text-foreground font-medium">Fee:</span> $20 USD (configurable, stored with 8 decimals). Converted to native currency at deployment time using Chainlink price feeds</div>
                                            <div><span className="text-foreground font-medium">How it works:</span> Receives compiled bytecode from the frontend and deploys it using the CREATE opcode. Fee is charged before deployment and sent to the fee recipient</div>
                                            <div><span className="text-foreground font-medium">Security:</span> Excess fee refund, Chainlink staleness validation, authorized relayer bypass, owner-only fee updates</div>
                                        </div>
                                    </div>

                                    {/* TokenLockerUpgradeable */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Lock className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">TokenLockerUpgradeable</h4>
                                                <span className="text-xs text-muted-foreground">TokenLockerUpgradeable.sol — UUPS Token Locking & Vesting</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> IERC20, SafeERC20, Initializable, UUPSUpgradeable, OwnableUpgradeable, ReentrancyGuardUpgradeable</div>
                                            <div><span className="text-foreground font-medium">Lock Types:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• <strong>Standard Lock:</strong> Fixed unlock time — tokens fully released after expiry</div>
                                                <div>• <strong>Vesting Lock:</strong> Linear release with configurable cliff period</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Features:</span> Lock extension, lock ownership transfer, LP token support, user lock tracking, token lock tracking</div>
                                            <div><span className="text-foreground font-medium">Fee:</span> Currently 0 (free to use)</div>
                                            <div><span className="text-foreground font-medium">Security:</span> ReentrancyGuard, SafeERC20, fee overpayment refund, input validation</div>
                                        </div>
                                    </div>

                                    {/* PresaleFactory */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Rocket className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">PresaleFactory + Presale</h4>
                                                <span className="text-xs text-muted-foreground">PresaleFactory.sol — Token Presale Deployer</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> IERC20, SafeERC20, ReentrancyGuard, Ownable</div>
                                            <div><span className="text-foreground font-medium">Factory:</span> Deploys individual Presale contracts per project. Tracks all presales and user presales</div>
                                            <div><span className="text-foreground font-medium">Presale Features:</span> Soft/hard caps, min/max contribution limits, configurable duration, claim/refund mechanism, finalization with liquidity allocation (min 51%)</div>
                                            <div><span className="text-foreground font-medium">Security:</span> ReentrancyGuard on contribute/claim/refund, SafeERC20, fee overpayment refund</div>
                                        </div>
                                    </div>

                                    {/* FairLaunchPresaleFactory */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Rocket className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">FairLaunchPresaleFactory</h4>
                                                <span className="text-xs text-muted-foreground">FairLaunchPresaleFactory.sol — UUPS Fair Launch Deployer</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">Inherits:</span> ChainlinkFeeManagerUpgradeable (UUPS + Chainlink USD fees)</div>
                                            <div><span className="text-foreground font-medium">Chainlink Integration:</span> USD fee conversion for creation fee ($150) and featured listing fee (+$150)</div>
                                            <div><span className="text-foreground font-medium">Features:</span> Fair launch model with equal token distribution, featured listing option, multi-payment token support</div>
                                            <div><span className="text-foreground font-medium">Security:</span> All fees charged via ChainlinkFeeManager with staleness validation and excess refund</div>
                                        </div>
                                    </div>

                                    {/* ContributionCollectorFactory */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Globe className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">ContributionCollectorFactory</h4>
                                                <span className="text-xs text-muted-foreground">ContributionCollectorFactory.sol — UUPS Cross-Chain Collector</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">Inherits:</span> Initializable, ChainlinkFeeManagerUpgradeable, UUPSUpgradeable</div>
                                            <div><span className="text-foreground font-medium">Chainlink Integration:</span> USD fee conversion for collector creation ($5)</div>
                                            <div><span className="text-foreground font-medium">Purpose:</span> Deploys ContributionCollector contracts on secondary chains (ETH, Polygon, Base) that link to primary chain presales</div>
                                            <div><span className="text-foreground font-medium">Features:</span> Cross-chain presale ID tracking, multi-payment token support, referral system</div>
                                            <div><span className="text-foreground font-medium">Security:</span> UUPS proxy, Chainlink fee validation, presale ID uniqueness check, authorized relayer bypass</div>
                                        </div>
                                    </div>

                                    {/* ChainlinkFeeManager */}
                                    <div className="p-5 rounded-xl bg-muted/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <Shield className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-sm">ChainlinkFeeManager (Base Contract)</h4>
                                                <span className="text-xs text-muted-foreground">ChainlinkFeeManager.sol — Abstract Fee Infrastructure</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 text-xs text-muted-foreground">
                                            <div><span className="text-foreground font-medium">Solidity:</span> ^0.8.20</div>
                                            <div><span className="text-foreground font-medium">OpenZeppelin Imports:</span> Ownable</div>
                                            <div><span className="text-foreground font-medium">Chainlink Integration:</span> AggregatorV3Interface — reads latestRoundData() for native/USD price</div>
                                            <div><span className="text-foreground font-medium">Key Functions:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• <code className="text-xs bg-muted px-1.5 py-0.5 rounded">getNativePrice()</code> — Returns native price in USD (8 decimals) with staleness validation</div>
                                                <div>• <code className="text-xs bg-muted px-1.5 py-0.5 rounded">getRequiredNative(usdAmount)</code> — Converts USD to native token amount at current price</div>
                                                <div>• <code className="text-xs bg-muted px-1.5 py-0.5 rounded">_chargeUsdFee(usdAmount)</code> — Charges fee, transfers to recipient, refunds excess</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Safety Mechanisms:</span></div>
                                            <div className="pl-3 space-y-0.5">
                                                <div>• Max staleness check (1 hour default) — rejects stale price data</div>
                                                <div>• Fallback price if Chainlink is down</div>
                                                <div>• Custom errors for gas-efficient reverts</div>
                                                <div>• Authorized relayer bypass for platform operations</div>
                                                <div>• Automatic excess payment refund</div>
                                            </div>
                                            <div><span className="text-foreground font-medium">Used by:</span> TokenDeployerProxy, PresaleFactory, FairLaunchPresaleFactory, ContributionCollectorFactory</div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 9.1 TECHNICAL DEEP DIVE ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="technical-deep-dive"
                            icon={Code2}
                            badge="Section 9.1"
                            title="Technical"
                            titleHighlight="Deep Dive"
                            description="Implementation-level explanations of core contract mechanisms, mathematical models, and security patterns."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-10">

                                {/* Reflection Mechanism */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <Coins className="h-5 w-5 text-primary" />
                                        1. Reflection Reward Mechanism
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            The TKS token implements a reflection model using a dual-balance system derived from
                                            the RFI (Reflect Finance) pattern. Every address maintains two balances:
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_rOwned</code> (reflection balance)
                                            and <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_tOwned</code> (true balance,
                                            only for excluded addresses). The reflection supply <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_rTotal</code> is
                                            initialized as <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">MAX - (MAX % _tTotal)</code> where
                                            MAX = <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">~uint256(0)</code> (2²⁵⁶ - 1).
                                        </p>
                                        <p>
                                            When a 1% reflection fee is charged on a transfer, the fee amount in reflection tokens is
                                            subtracted from <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_rTotal</code> without
                                            changing <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_tTotal</code>. This reduces the
                                            conversion rate (<code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_rTotal / _tTotal</code>),
                                            causing every holder&apos;s <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">balanceOf()</code> to
                                            return a proportionally larger value — distributing rewards without any gas-consuming iteration
                                            over holder addresses [4].
                                        </p>
                                        <p>
                                            Addresses excluded from reflection (DEX pairs, contract addresses) use <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_tOwned</code> directly,
                                            preventing LP pools from accumulating reflection rewards that would distort trading ratios.
                                        </p>
                                    </div>
                                </div>

                                {/* Cross-Chain Claims */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <Globe className="h-5 w-5 text-primary" />
                                        2. Cross-Chain Claim Verification
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            The TKSClaimVault (v1.3.0) implements two claim methods. The signature-based method
                                            constructs a message hash from five parameters:
                                        </p>
                                        <div className="p-4 rounded-lg bg-muted/40 font-mono text-xs">
                                            <div className="text-primary mb-1">// Signature message construction</div>
                                            <div>messageHash = keccak256(abi.encodePacked(</div>
                                            <div className="pl-4">msg.sender,&nbsp;&nbsp;&nbsp;// claimer address</div>
                                            <div className="pl-4">amount,&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;// total claimable TKS</div>
                                            <div className="pl-4">nonce,&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;// sequential nonce</div>
                                            <div className="pl-4">block.chainid,// prevents cross-chain replay</div>
                                            <div className="pl-4">address(this) // prevents cross-contract replay</div>
                                            <div>))</div>
                                        </div>
                                        <p>
                                            The hash is wrapped with <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">MessageHashUtils.toEthSignedMessageHash()</code> and
                                            recovered via <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">ECDSA.recover()</code> [5].
                                            The recovered signer must equal <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">owner()</code>.
                                            Including <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">block.chainid</code> and
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">address(this)</code> in the hash prevents
                                            a signature valid on BSC from being replayed on Ethereum, and vice versa. The per-user nonce
                                            increments atomically on each claim, preventing signature reuse [5][6].
                                        </p>
                                        <p>
                                            The <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">claimExact()</code> variant signs
                                            the exact payout amount rather than total claimable, eliminating race conditions when multiple
                                            contributions arrive between signature generation and on-chain execution. Tokens are transferred
                                            via <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">SafeERC20.safeTransfer()</code> [3]
                                            with ReentrancyGuard protection on all claim functions.
                                        </p>
                                    </div>
                                </div>

                                {/* Contribution Pipeline */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                        3. Contribution-to-Claim Pipeline
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            When a user contributes to the TKS presale, the TKSContributionCollector executes the
                                            following atomic flow within a single transaction:
                                        </p>
                                        <div className="space-y-2">
                                            {[
                                                "1. Validate: Check payment token is accepted, amount > 0, contract not paused",
                                                "2. Price: For native tokens → query Chainlink latestRoundData() for USD conversion (8 decimals). For stablecoins → normalize decimals via IERC20Metadata [4]",
                                                "3. Calculate: tksAmount = (usdValue × tksPerUsd) / 10^8 where tksPerUsd defaults to 10e18 (10 TKS per $1)",
                                                "4. Referral: If referrer exists and ≠ msg.sender, calculate referralTks = tksAmount × referralPercent / 100 (max 20%)",
                                                "5. Track: Update contributionsUsd[user], tksEarned[user], totalRaisedUsd, totalTksSold",
                                                "6. Auto-sync: If claimVault is configured, call ITKSClaimVault.increaseClaimable(user, tksAmount) via try/catch",
                                                "7. Transfer: Forward funds to fundsReceiver immediately via call{value:} (native) or safeTransfer (ERC-20)"
                                            ].map((step, i) => (
                                                <div key={i} className="flex gap-2 text-xs">
                                                    <span className="text-primary shrink-0">→</span>
                                                    <span>{step}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <p>
                                            The critical design decision is step 7: funds transfer to the project wallet within the
                                            same transaction. The contract never custodies user funds beyond a single block, eliminating
                                            the attack surface of holding a pool of contributions.
                                        </p>
                                    </div>
                                </div>

                                {/* Staking Rewards */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <TrendingUp className="h-5 w-5 text-primary" />
                                        4. Staking Reward Calculation
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            The TKSStakingMaster computes rewards using continuous linear accrual. The reward formula
                                            for any given moment is:
                                        </p>
                                        <div className="p-4 rounded-lg bg-muted/40 font-mono text-xs text-center">
                                            rewards = (stakedAmount × APY × timeElapsed) / (10000 × 365 days)
                                        </div>
                                        <p>
                                            Where APY is stored in basis points (e.g., 800 = 8%, 4000 = 40%) and timeElapsed is measured
                                            in seconds from <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">lastClaimAt</code>.
                                            The denominator <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">10000 × 365 days</code> converts
                                            annual basis-point rate to a per-second multiplier. Each pool maps duration indices to APY
                                            tiers via the <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">poolApyTiers</code> mapping.
                                        </p>
                                        <p>
                                            The TKS reward token is set as <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">immutable</code> in
                                            the constructor — it cannot be changed after deployment, guaranteeing that reward payouts are
                                            always denominated in TKS regardless of future contract state changes. The
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">recoverToken()</code> function explicitly
                                            blocks recovery of both the TKS reward token and any active staking tokens, preventing owner
                                            drain of user funds [3].
                                        </p>
                                    </div>
                                </div>

                                {/* Token Deployment */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <Code2 className="h-5 w-5 text-primary" />
                                        5. Token Deployment via CREATE Opcode
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            The TokenDeployerProxy uses inline assembly to deploy user-submitted bytecode via the
                                            EVM <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">CREATE</code> opcode. The deployment
                                            sequence operates at the assembly level:
                                        </p>
                                        <div className="p-4 rounded-lg bg-muted/40 font-mono text-xs">
                                            <div className="text-primary mb-1">// Assembly deployment</div>
                                            <div>let ptr := mload(0x40)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;// free memory pointer</div>
                                            <div>calldatacopy(ptr, offset, len) // copy bytecode to memory</div>
                                            <div>tokenAddress := create(0, ptr, len) // deploy with 0 ETH value</div>
                                        </div>
                                        <p>
                                            Before deployment, <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">_chargeUsdFee(creationFeeUsd)</code> is
                                            called, which queries the Chainlink price feed via <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">getNativePrice()</code>,
                                            calculates the required native amount as <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">(usdAmount × 1e18) / nativePrice</code>,
                                            transfers the fee to <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">feeRecipient</code>,
                                            and refunds any excess <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">msg.value</code> to
                                            the caller [7][12]. The Chainlink price feed includes a 3-step fallback: (1) live oracle price,
                                            (2) cached fallback price if oracle returns invalid/stale data, (3) revert with custom error
                                            if no fallback is configured.
                                        </p>
                                    </div>
                                </div>

                                {/* Vesting Math */}
                                <div>
                                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                                        <Lock className="h-5 w-5 text-primary" />
                                        6. Vesting Lock Release Formula
                                    </h3>
                                    <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                                        <p>
                                            The TokenLockerUpgradeable contract implements two lock types. Standard locks use a
                                            binary release — zero tokens available before <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">unlockTime</code>,
                                            full amount after. Vesting locks use linear interpolation with an optional cliff:
                                        </p>
                                        <div className="p-4 rounded-lg bg-muted/40 font-mono text-xs">
                                            <div className="text-primary mb-1">// Vesting claimable calculation</div>
                                            <div>if (now &lt; cliffTime) return 0;</div>
                                            <div>if (now &gt;= unlockTime) return amount - released;</div>
                                            <div>vested = (amount × (now - startTime)) / duration;</div>
                                            <div>claimable = vested - released;</div>
                                        </div>
                                        <p>
                                            The <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">Lock</code> struct stores:
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">amount</code> (total locked),
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">released</code> (already claimed),
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">startTime</code>,
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">cliffTime</code>,
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">duration</code>, and
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">unlockTime</code>.
                                            Lock ownership is transferable via <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">transferLock()</code> which
                                            updates the <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">userLocks</code> mapping
                                            atomically — removing from the old owner and adding to the new owner [9].
                                            Lock extensions are one-directional only: <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">newUnlockTime</code> must
                                            exceed the current <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">unlockTime</code>,
                                            preventing retroactive shortening.
                                        </p>
                                    </div>
                                </div>

                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 10. ARCHITECTURE ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="architecture"
                            icon={Layers}
                            badge="Section 10"
                            title="Technical"
                            titleHighlight="Architecture"
                            description="A multi-layered architecture designed for scalability, security, and cross-chain interoperability."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-8">
                                {[
                                    {
                                        title: "Smart Contract Layer",
                                        icon: FileCode,
                                        desc: "Nine core Solidity contracts (^0.8.20/^0.8.33) deployed identically across BSC and Ethereum using Hardhat with deterministic deployment. UUPS proxy pattern [10] enables non-breaking upgrades while preserving contract state and user balances.",
                                        tags: ["Solidity 0.8.33", "UUPS Proxy", "OpenZeppelin v5", "Hardhat"],
                                    },
                                    {
                                        title: "Oracle Integration",
                                        icon: TrendingUp,
                                        desc: "ChainlinkFeeManager and ChainlinkFeeManagerUpgradeable base contracts wrap AggregatorV3Interface [12] with staleness validation (3600s), fallback pricing, and automatic excess refund. Used by 4 of 9 core contracts for USD→native fee conversion.",
                                        tags: ["AggregatorV3", "1hr Staleness", "Fallback Price", "ETH/BNB/MATIC feeds"],
                                    },
                                    {
                                        title: "Cross-Chain Architecture",
                                        icon: Globe,
                                        desc: "ContributionCollectorFactory deploys chain-specific collectors linked to BSC presale IDs. The TKSClaimVault on BSC/ETH validates ECDSA signatures with chain-scoped nonces [5], enabling claim resolution without third-party bridge dependencies.",
                                        tags: ["BSC Primary", "ETH/Polygon/Base Collectors", "ECDSA Claims", "No Bridge"],
                                    },
                                    {
                                        title: "Frontend & API",
                                        icon: Code2,
                                        desc: "Next.js 16 with React 19 server components for SEO-critical pages. Reown AppKit handles multi-wallet connectivity (MetaMask, WalletConnect, Coinbase, Phantom). Prisma ORM manages off-chain state with PostgreSQL. RESTful API routes handle signature generation and cross-chain sync.",
                                        tags: ["Next.js 16", "React 19", "AppKit", "Prisma/PostgreSQL"],
                                    },
                                ].map((layer) => {
                                    const LIcon = layer.icon
                                    return (
                                        <div key={layer.title} className="flex gap-5 p-5 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                            <div className="h-12 w-12 shrink-0 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                                                <LIcon className="h-6 w-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold mb-1">{layer.title}</h4>
                                                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{layer.desc}</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {layer.tags.map((tag) => (
                                                        <span key={tag} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 11. REVENUE & ECOSYSTEM ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="revenue"
                            icon={CircleDollarSign}
                            badge="Section 11"
                            title="Revenue &"
                            titleHighlight="Ecosystem"
                            description="A self-sustaining ecosystem where multiple revenue streams continuously increase TKS value."
                        />
                        <div className="max-w-5xl mx-auto space-y-8">
                            {/* Revenue Sources */}
                            <motion.div
                                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <FeatureCard icon={Building2} title="Subsidiary Revenue" description="TokenKickstarter establishes strategic subsidiary ventures. All proceeds are reinvested back into the ecosystem." />
                                <FeatureCard icon={Coins} title="Platform Fees" description="Revenue from token creation, presale launches, token locking, and premium features." />
                                <FeatureCard icon={TrendingUp} title="Trading Fees" description="Nominal fees on ecosystem transactions contribute to the treasury." />
                                <FeatureCard icon={Megaphone} title="Marketing Investment" description="Strategic marketing spend to drive adoption, partnerships, and brand awareness." />
                                <FeatureCard icon={Code2} title="Product Development" description="Continuous investment in new DeFi features, tools, and cross-chain expansion." />
                                <FeatureCard icon={CircleDollarSign} title="Buy-Back & Burn" description="Ecosystem revenue used for periodic buy-back and burn events to reduce supply and increase value." />
                            </motion.div>

                            {/* Revenue Flow */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="font-bold text-xl mb-6 text-center">Revenue Cycle</h3>
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                                    {[
                                        { step: 1, title: "Generate", desc: "Subsidiary ventures & platform fees generate revenue" },
                                        { step: 2, title: "Reinvest", desc: "Revenue funneled into marketing & development" },
                                        { step: 3, title: "Grow", desc: "Ecosystem expansion increases token utility" },
                                        { step: 4, title: "Appreciate", desc: "Buy-back & burn increases TKS value" },
                                    ].map((item, i) => (
                                        <div key={item.step} className="relative">
                                            <div className="flex flex-col items-center text-center p-6 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                                <div className="h-14 w-14 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-xl mb-4 group-hover:scale-110 transition-all">
                                                    {item.step}
                                                </div>
                                                <h4 className="font-bold mb-1">{item.title}</h4>
                                                <p className="text-sm text-muted-foreground">{item.desc}</p>
                                            </div>
                                            {i < 3 && (
                                                <div className="hidden lg:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                                                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ═══ 11a. DONATION CAMPAIGNS ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="donation-campaigns"
                            icon={Heart}
                            badge="Section 11a"
                            title="Donation"
                            titleHighlight="Campaigns"
                            description="A decentralized crypto fundraising platform for causes, charities, open source, and personal needs — powered by smart contracts."
                        />
                        <div className="max-w-5xl mx-auto space-y-8">

                            {/* Overview */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                    <Heart className="h-5 w-5 text-primary" />
                                    Start Fundraising with Crypto
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    TokenKickstarter&apos;s donation platform enables anyone to create a fundraising campaign and accept
                                    donations in crypto — BNB, ETH, MATIC, USDT, USDC, and more. Campaign creators tell their story,
                                    set an optional goal amount and deadline, upload branding, and publish. Donors can contribute
                                    from any supported chain with full transparency via on-chain transactions.
                                </p>

                                {/* Categories */}
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Layers className="h-4 w-4 text-primary" />
                                    Campaign Categories
                                </h4>
                                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 mb-6">
                                    {[
                                        "Medical", "Education", "Community", "Open Source",
                                        "Disaster Relief", "Charity", "Personal", "Animal Welfare",
                                        "Environment", "Memorial", "Other",
                                    ].map((cat) => (
                                        <div key={cat} className="flex items-center gap-2 text-sm">
                                            <Zap className="h-3.5 w-3.5 text-primary shrink-0" />
                                            <span className="text-muted-foreground">{cat}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Creation Flow */}
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Rocket className="h-4 w-4 text-primary" />
                                    Campaign Creation Flow
                                </h4>
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                                    {[
                                        { step: 1, title: "Choose Your Cause", desc: "Select a category and name your campaign" },
                                        { step: 2, title: "Tell Your Story", desc: "Add description, cover image, logo, and social links" },
                                        { step: 3, title: "Set Your Goal", desc: "Define target amount, currency, and optional deadline" },
                                        { step: 4, title: "Review & Publish", desc: "Preview and launch — immediately accepting donations" },
                                    ].map((item) => (
                                        <div key={item.step} className="flex flex-col items-center text-center p-4 rounded-xl bg-muted/30">
                                            <div className="h-10 w-10 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg mb-3">
                                                {item.step}
                                            </div>
                                            <h5 className="font-bold text-sm mb-1">{item.title}</h5>
                                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Fee Model */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <CircleDollarSign className="h-5 w-5 text-primary" />
                                    Fee Model — DonateSplitter Smart Contract
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    Donations are processed through the DonateSplitter smart contract which transparently splits
                                    incoming funds between the campaign recipient and the platform. Campaign creators choose one
                                    of two fee models at campaign creation time:
                                </p>
                                <div className="grid gap-4 sm:grid-cols-2 mb-6">
                                    <div className="p-4 rounded-xl bg-muted/20 border border-muted/30">
                                        <h4 className="text-sm font-bold mb-2">Percentage Model (Default)</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            5% platform fee (500 basis points) deducted from each donation via the DonateSplitter
                                            contract. 95% goes directly to the campaign recipient in the same transaction.
                                        </p>
                                        <div className="mt-3 flex items-center gap-2 text-xs font-mono bg-muted/30 px-3 py-1.5 rounded-lg">
                                            <span className="text-primary font-semibold">95%</span>
                                            <span className="text-muted-foreground">→ Recipient</span>
                                            <span className="mx-1 text-muted-foreground/40">|</span>
                                            <span className="text-primary font-semibold">5%</span>
                                            <span className="text-muted-foreground">→ Platform</span>
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/20 border border-muted/30">
                                        <h4 className="text-sm font-bold mb-2">One-Time Fee Model</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            A one-time $100 USD fee (paid in native token via Chainlink price feed conversion)
                                            is paid at campaign creation. All subsequent donations go 100% to the recipient
                                            with no contract interaction — direct wallet-to-wallet transfers.
                                        </p>
                                        <div className="mt-3 flex items-center gap-2 text-xs font-mono bg-muted/30 px-3 py-1.5 rounded-lg">
                                            <span className="text-primary font-semibold">$100</span>
                                            <span className="text-muted-foreground">upfront → 0% ongoing fees</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Technical details */}
                                <div className="grid gap-4 sm:grid-cols-2">
                                    {[
                                        "DonateSplitter contract on BSC, Ethereum, Polygon, and Base",
                                        "Supports native tokens (BNB, ETH, POL) and ERC-20 stablecoins (USDT, USDC)",
                                        "Chainlink AggregatorV3 price feeds for USD fee conversion",
                                        "On-chain DonationNative and DonationToken events for full transparency",
                                        "Optional KYC verification for campaign creators",
                                        "Goal tracking with progress bars and donor counts",
                                        "Category-based discovery with search and filtering",
                                        "Custom campaign slugs and shareable URLs",
                                    ].map((feat, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm">
                                            <Zap className="h-4 w-4 text-primary shrink-0" />
                                            <span className="text-muted-foreground">{feat}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </section>

                {/* ═══ 11b. AIRDROP QUESTS ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="airdrop-quests"
                            icon={Gift}
                            badge="Section 11b"
                            title="Airdrop"
                            titleHighlight="Quests"
                            description="Quest-based token distribution — earn crypto rewards by completing social, content, and on-chain tasks."
                        />
                        <div className="max-w-5xl mx-auto space-y-8">

                            {/* Overview */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                    <Gift className="h-5 w-5 text-primary" />
                                    Quest-Based Airdrop Platform
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    The TokenKickstarter airdrop system lets any project create quest-based campaigns where users
                                    earn crypto rewards (ETH, BNB, USDT, USDC, DAI, or any ERC-20 token) by completing verifiable tasks.
                                    Campaign creators deposit reward tokens into a smart contract, define quests, and set distribution
                                    parameters. Participants complete quests and claim rewards on-chain.
                                </p>

                                {/* Campaign Creation Flow */}
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Rocket className="h-4 w-4 text-primary" />
                                    Campaign Creation (5-Step Wizard)
                                </h4>
                                <div className="grid gap-4 sm:grid-cols-5 mb-6">
                                    {[
                                        { step: 1, title: "Campaign Info", desc: "Title, description, logo, website" },
                                        { step: 2, title: "Reward Setup", desc: "Token, amount, max winners, distribution model" },
                                        { step: 3, title: "Quest Builder", desc: "Add and configure social/on-chain tasks" },
                                        { step: 4, title: "Timing", desc: "Start and end dates" },
                                        { step: 5, title: "Deploy", desc: "Review and deploy on-chain" },
                                    ].map((item) => (
                                        <div key={item.step} className="flex flex-col items-center text-center p-3 rounded-xl bg-muted/30">
                                            <div className="h-8 w-8 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm mb-2">
                                                {item.step}
                                            </div>
                                            <h5 className="font-bold text-xs mb-0.5">{item.title}</h5>
                                            <p className="text-[10px] text-muted-foreground leading-tight">{item.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Quest Types */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <Layers className="h-5 w-5 text-primary" />
                                    Quest Types
                                </h3>

                                {/* Social quests */}
                                <div className="mb-6">
                                    <h4 className="text-sm font-semibold mb-3 text-primary">Social Quests</h4>
                                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {[
                                            "Follow on X (Twitter)",
                                            "Follow on Instagram",
                                            "Follow on TikTok",
                                            "Follow on Facebook",
                                            "Join Telegram Group",
                                            "Join Discord Server",
                                            "Subscribe on YouTube",
                                        ].map((quest) => (
                                            <div key={quest} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/20">
                                                <Zap className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{quest}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Content quests */}
                                <div className="mb-6">
                                    <h4 className="text-sm font-semibold mb-3 text-primary">Content Quests</h4>
                                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {[
                                            "Repost X Post",
                                            "Share Facebook Post",
                                            "Like X Post",
                                            "Like Instagram Post",
                                            "Post on X (with tag)",
                                            "Comment on X Post",
                                            "Watch YouTube Video",
                                        ].map((quest) => (
                                            <div key={quest} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/20">
                                                <Zap className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{quest}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* On-chain & Custom quests */}
                                <div>
                                    <h4 className="text-sm font-semibold mb-3 text-primary">On-Chain &amp; Custom Quests</h4>
                                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                        {[
                                            "Hold Token (minimum balance check)",
                                            "Visit Website",
                                            "Buy from Presale ($10+ purchase)",
                                            "Custom Task (creator-defined)",
                                        ].map((quest) => (
                                            <div key={quest} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/20">
                                                <Zap className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-muted-foreground">{quest}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>

                            {/* Distribution Models & Technical Details */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-primary" />
                                    Distribution &amp; Smart Contracts
                                </h3>
                                <div className="grid gap-4 sm:grid-cols-2 mb-6">
                                    <div className="p-4 rounded-xl bg-muted/20 border border-muted/30">
                                        <h4 className="text-sm font-bold mb-2">First-Come, First-Served</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            Rewards are distributed to the first N participants who complete all required quests.
                                            Each participant claims their reward immediately upon task verification.
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-xl bg-muted/20 border border-muted/30">
                                        <h4 className="text-sm font-bold mb-2">Raffle</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            All qualifying participants enter a raffle. Winners are randomly selected after the
                                            campaign ends. Rewards are claimed from the smart contract by winners.
                                        </p>
                                    </div>
                                </div>

                                <div className="grid gap-4 sm:grid-cols-2">
                                    {[
                                        "Reward tokens locked in AirdropQuest smart contract on deployment",
                                        "Supports ETH, BNB, USDT, USDC, DAI, and any ERC-20 token",
                                        "Per-task or claim-all reward distribution modes",
                                        "Deployed on Ethereum (Chain 1) and BSC (Chain 56)",
                                        "AirdropFactory contract for campaign deployment",
                                        "On-chain participant tracking and quest completion verification",
                                        "Configurable max winners and reward-per-winner amounts",
                                        "Official pinned campaigns for TKS ecosystem quests",
                                    ].map((feat, i) => (
                                        <div key={i} className="flex items-center gap-3 text-sm">
                                            <Zap className="h-4 w-4 text-primary shrink-0" />
                                            <span className="text-muted-foreground">{feat}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </section>

                {/* ═══ 12. TKS LAYER 1 BLOCKCHAIN & VALIDATORS ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="tks-blockchain"
                            icon={Server}
                            badge="Section 12 — TKS Chain"
                            title="TKS Layer 1"
                            titleHighlight="Blockchain & Validators"
                            description="A custom Substrate-based Layer 1 blockchain purpose-built for privacy, payments, and presales — powering the unified TKS + Cipher ecosystem."
                        />

                        {/* Architecture Overview */}
                        <motion.div
                            className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto"
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Layers className="h-5 w-5 text-primary" />
                                    Chain Architecture
                                </h3>
                                <div className="space-y-4">
                                    {[
                                        { icon: Server, title: "Substrate Runtime", desc: "Built on Polkadot SDK (Substrate) with BABE + GRANDPA consensus — producing ~6 second blocks with deterministic finality." },
                                        { icon: Cpu, title: "Full EVM Compatibility", desc: "Frontier EVM integration enables MetaMask, Remix, and Solidity support. Deploy ERC-20, NFTs, and DAOs — gas paid in ultra-cheap TKS." },
                                        { icon: Network, title: "P2P Storage Swarm", desc: "libp2p-powered temporary encrypted message storage with GossipSub discovery. Messages auto-delete after delivery or 30 days." },
                                        { icon: MessageSquareLock, title: "Cipher Integration", desc: "The TKS chain powers Cipher's identity registry, username lookups, and in-app wallet — one chain for messaging, payments, and presales." },
                                    ].map((u) => {
                                        const UIcon = u.icon
                                        return (
                                            <div key={u.title} className="flex gap-4 p-4 rounded-xl bg-muted/30 group hover:bg-muted/50 transition-all">
                                                <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-all">
                                                    <UIcon className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold mb-1">{u.title}</h4>
                                                    <p className="text-sm text-muted-foreground">{u.desc}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </motion.div>

                            <motion.div variants={fadeUp} className="glass-card p-8">
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-primary" />
                                    Consensus & Security
                                </h3>
                                <div className="space-y-3">
                                    <InfoRow label="Consensus" value="Nominated Proof-of-Stake (NPoS)" />
                                    <InfoRow label="Block Time" value="~6 seconds" />
                                    <InfoRow label="Finality" value="GRANDPA (deterministic)" />
                                    <InfoRow label="EVM Chain ID" value="7331" />
                                    <InfoRow label="Block Gas Limit" value="30,000,000" />
                                    <InfoRow label="Slash Penalty" value="5% stake (downtime) / 100% (malicious)" />
                                    <InfoRow label="Unbonding Period" value="7 days" />
                                    <InfoRow label="Inflation" value="2–5% annual (rewards pool)" />
                                </div>
                            </motion.div>
                        </motion.div>

                        {/* Validator Program */}
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-5xl mx-auto mt-8"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                <Users className="h-5 w-5 text-primary" />
                                Validator Program — Become a TKS Validator
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                The TKS Layer 1 blockchain is secured by a decentralized network of validators.
                                <strong className="text-foreground"> Anyone can become a validator</strong> by staking the required amount of TKS tokens
                                and running a full node. Validator seats are <strong className="text-foreground">limited</strong> — early participants
                                secure their position while seats remain available.
                            </p>

                            <div className="grid gap-6 md:grid-cols-3 mb-8">
                                <div className="p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-center">
                                    <div className="text-3xl font-bold text-primary mb-1">100,000</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">TKS Required to Validate</div>
                                    <p className="text-xs text-muted-foreground">Minimum stake to register as a validator and produce blocks on the TKS network.</p>
                                </div>
                                <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 text-center">
                                    <div className="text-3xl font-bold text-emerald-500 mb-1">Up to 35%</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Validator APY</div>
                                    <p className="text-xs text-muted-foreground">Staking rewards cover VPS costs and generate profit — self-sustaining like Bitcoin mining.</p>
                                </div>
                                <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 text-center">
                                    <div className="text-3xl font-bold text-amber-500 mb-1">Limited</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Validator Seats</div>
                                    <p className="text-xs text-muted-foreground">Validator seats are finite. Early validators secure positions before the network reaches capacity.</p>
                                </div>
                            </div>

                            <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                                <Server className="h-4 w-4 text-primary" />
                                Node Requirements
                            </h4>
                            <div className="overflow-x-auto rounded-xl border border-border/30">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-muted/30">
                                            <th className="text-left p-3 font-semibold">Resource</th>
                                            <th className="text-left p-3 font-semibold">Minimum</th>
                                            <th className="text-left p-3 font-semibold">Recommended</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/20">
                                        {[
                                            { resource: "CPU", min: "4 vCPU", rec: "8 vCPU" },
                                            { resource: "RAM", min: "8 GB", rec: "16 GB" },
                                            { resource: "Storage", min: "500 GB SSD", rec: "1 TB NVMe" },
                                            { resource: "Bandwidth", min: "1 Gbps", rec: "Unmetered" },
                                            { resource: "TKS Stake", min: "100,000 TKS", rec: "More = higher rewards" },
                                            { resource: "Est. Monthly Cost", min: "~$15 (Hetzner)", rec: "~$25 (OVH/Contabo)" },
                                        ].map((row, i) => (
                                            <tr key={i} className="hover:bg-muted/20 transition-colors">
                                                <td className="p-3 font-medium">{row.resource}</td>
                                                <td className="p-3 text-muted-foreground">{row.min}</td>
                                                <td className="p-3 text-muted-foreground">{row.rec}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                <div className="space-y-3">
                                    <h4 className="font-bold text-sm">Revenue Flows to Validators</h4>
                                    {[
                                        "Username registration fees → Treasury → Validator staking rewards",
                                        "EVM gas fees → Direct to block producer",
                                        "Inflation (2–5% annual) → Staking reward pool",
                                        "Cipher usage fees → Ecosystem revenue → Buyback & distribute",
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                            <span className="text-muted-foreground">{item}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-3">
                                    <h4 className="font-bold text-sm">Validator Responsibilities</h4>
                                    {[
                                        "Produce and validate blocks on the TKS network",
                                        "Store and relay encrypted messages for offline Cipher delivery",
                                        "Maintain minimum 99.5% uptime to avoid slashing",
                                        "Support EVM RPC endpoints for dApp and wallet connectivity",
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <ArrowRight className="h-3 w-3 text-primary shrink-0" />
                                            <span className="text-muted-foreground">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Validator Rewards */}
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-5xl mx-auto mt-8"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                <TrendingUp className="h-5 w-5 text-primary" />
                                Validator Rewards & Earning Potential
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                TKS validators earn rewards from <strong className="text-foreground">multiple revenue streams</strong> simultaneously.
                                Unlike single-source staking models, the TKS Layer 1 architecture routes fees from every ecosystem
                                activity — presales, token deployments, Cipher messaging, and EVM transactions — back to active validators.
                            </p>

                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                                {[
                                    {
                                        title: "Block Production Rewards",
                                        value: "Up to 35% APY",
                                        desc: "Earned proportionally to your stake and lock duration. Validators producing blocks receive freshly minted TKS from the network's inflation pool — longer commitments earn higher yields.",
                                        color: "text-primary",
                                    },
                                    {
                                        title: "EVM Gas Fees",
                                        value: "Per-Block",
                                        desc: "Every EVM transaction (token transfers, smart contract calls, NFT mints) pays gas in TKS. The block producer receives 100% of the gas fee for their blocks.",
                                        color: "text-emerald-500",
                                    },
                                    {
                                        title: "Username Registration Fees",
                                        value: "~10 TKS Each",
                                        desc: "Every Cipher user who registers an @username pays a small on-chain fee. These fees flow to the treasury and are redistributed to active validators.",
                                        color: "text-blue-500",
                                    },
                                    {
                                        title: "Cipher Relay Fees",
                                        value: "Per-Message",
                                        desc: "Validators store and relay encrypted messages for offline Cipher users. Micro-fees are collected for storage swarm participation and distributed to staked nodes.",
                                        color: "text-violet-500",
                                    },
                                    {
                                        title: "Presale & Platform Fees",
                                        value: "Revenue Share",
                                        desc: "A portion of platform revenue from presale creation ($150), token deployments ($20), and premium features is allocated to the validator reward pool via buybacks.",
                                        color: "text-amber-500",
                                    },
                                    {
                                        title: "Compounding Staking",
                                        value: "Auto-Compound",
                                        desc: "Validators can re-stake earned rewards to increase their stake weight, earning progressively higher rewards as their position grows — fully on-chain and permissionless.",
                                        color: "text-pink-500",
                                    },
                                ].map((reward, i) => (
                                    <div key={i} className="p-5 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all">
                                        <div className={`text-lg font-bold mb-1 ${reward.color}`}>{reward.value}</div>
                                        <h4 className="font-semibold text-sm mb-2">{reward.title}</h4>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{reward.desc}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="p-5 rounded-xl bg-gradient-to-r from-primary/5 to-emerald-500/5 border border-primary/20">
                                <h4 className="font-bold text-sm mb-3 flex items-center gap-2">
                                    <Zap className="h-4 w-4 text-primary" />
                                    Example Validator Earnings (Estimated)
                                </h4>
                                <div className="overflow-x-auto rounded-lg border border-border/20">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-muted/30">
                                                <th className="text-left p-3 font-semibold">Revenue Source</th>
                                                <th className="text-left p-3 font-semibold">Est. Monthly</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/20">
                                            {[
                                                { source: "Block rewards (staking APY on 100K TKS)", est: "~833–1,250 TKS" },
                                                { source: "EVM gas fees (block production share)", est: "Variable" },
                                                { source: "Username registration fee share", est: "Variable (grows with Cipher adoption)" },
                                                { source: "Cipher relay & storage micro-fees", est: "Variable (grows with message volume)" },
                                                { source: "Platform revenue buyback distribution", est: "Proportional to stake" },
                                            ].map((row, i) => (
                                                <tr key={i} className="hover:bg-muted/20 transition-colors">
                                                    <td className="p-3 text-muted-foreground">{row.source}</td>
                                                    <td className="p-3 font-medium">{row.est}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-muted-foreground mt-3">
                                    <strong className="text-foreground">Note:</strong> VPS operating costs (~$15–25/month) are designed to be fully covered by staking rewards alone.
                                    Additional revenue from gas fees, relay fees, and platform revenue sharing represents pure profit for validators.
                                    Rewards increase proportionally with higher stake amounts.
                                </p>
                            </div>
                        </motion.div>

                        {/* Cipher Ecosystem */}
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-5xl mx-auto mt-8"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                <MessageSquareLock className="h-5 w-5 text-primary" />
                                Cipher — Decentralized Private Messenger
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                Cipher is a fully decentralized, anonymous messaging application powered by the TKS Layer 1
                                blockchain. With end-to-end encryption (Double Ratchet + AES-256-GCM), zero central servers,
                                and optional global usernames registered on-chain, Cipher represents the next evolution of
                                private communication.
                            </p>
                            <div className="grid gap-4 sm:grid-cols-2">
                                {[
                                    "100% E2EE — Double Ratchet + AES-256-GCM encryption",
                                    "No phone, email, or personal info required",
                                    "Optional @username registered on TKS chain",
                                    "Built-in TKS wallet for sending/receiving crypto",
                                    "Offline delivery via decentralized node swarms",
                                    "IP hiding via Nym mixnet integration",
                                    "10GB+ file transfers — encrypted and temporary",
                                    "Post-quantum encryption ready (Kyber hybrid)",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                        <Zap className="h-4 w-4 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
                                <p className="text-sm text-muted-foreground">
                                    <strong className="text-foreground">Learn more:</strong> Visit{" "}
                                    <a href="https://cipher.llc" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                                        cipher.llc
                                    </a>{" "}
                                    for the full Cipher documentation and roadmap.
                                </p>
                            </div>
                        </motion.div>

                        {/* Cipher CLI */}
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-5xl mx-auto mt-8"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                                <Code2 className="h-5 w-5 text-primary" />
                                Cipher CLI — Terminal Messaging Client
                            </h3>
                            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary">
                                <Zap className="h-3 w-3" /> v0.1.0 Released
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                Cipher CLI brings the full Cipher encrypted messaging experience to the terminal. Built in Rust,
                                it uses the same Double Ratchet + AES-256-GCM encryption and WSS relay transport as the Cipher
                                mobile app. Generate a BIP-39 identity, register @usernames on the TKS blockchain, send end-to-end
                                encrypted messages, transfer files in 2MB encrypted chunks with Merkle tree integrity verification,
                                and enter interactive bidirectional chat mode — all from the command line.
                            </p>

                            {/* Architecture */}
                            <div className="mb-6 p-4 rounded-xl bg-muted/20 border border-muted/30">
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Layers className="h-4 w-4 text-primary" />
                                    Architecture
                                </h4>
                                <pre className="text-xs font-mono text-muted-foreground leading-relaxed overflow-x-auto whitespace-pre">{`cipher-cli
├── src/
│   ├── main.rs          ← CLI entry (clap subcommands)
│   ├── identity_store.rs ← Encrypted identity (~/.cipher/identity.json)
│   └── commands/
│       ├── init.rs       ← Generate identity (BIP-39)
│       ├── restore.rs    ← Restore from seed phrase
│       ├── whoami.rs     ← Show addresses & public keys
│       ├── send.rs       ← Send E2EE message
│       ├── send_file.rs  ← Send encrypted file
│       ├── recv.rs       ← Receive messages
│       ├── chat.rs       ← Interactive bidirectional chat
│       ├── register.rs   ← Register @username on TKS
│       ├── lookup.rs     ← Resolve @username on-chain
│       ├── balance.rs    ← Check TKS balance
│       ├── node_info.rs  ← Query blockchain node
│       ├── contacts.rs   ← List conversation peers
│       ├── status.rs     ← Network status
│       └── export.rs     ← Export seed phrase
└── Cargo.toml`}</pre>
                            </div>

                            {/* Commands table */}
                            <div className="mb-6">
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-primary" />
                                    Commands
                                </h4>
                                <div className="rounded-xl border border-muted/30 overflow-hidden">
                                    <div className="grid grid-cols-[140px_1fr] text-xs font-semibold bg-muted/30 px-4 py-2 border-b border-muted/20">
                                        <span>Command</span>
                                        <span>Description</span>
                                    </div>
                                    {[
                                        ["init", "Generate new identity (BIP-39 seed phrase)"],
                                        ["restore", "Restore identity from existing seed phrase"],
                                        ["whoami", "Display your addresses & public keys"],
                                        ["export", "Export seed phrase (with warning)"],
                                        ["send", "Send encrypted text message"],
                                        ["send-file", "Send encrypted file (image, video, document)"],
                                        ["recv", "Poll for incoming messages"],
                                        ["recv -f", "Live listen mode (streaming)"],
                                        ["chat", "Interactive bidirectional chat"],
                                        ["contacts", "List conversation peers"],
                                        ["status", "Network & transport status"],
                                        ["register", "Register @username on TKS blockchain (FREE)"],
                                        ["lookup", "Resolve @username ↔ address on-chain"],
                                        ["balance", "Check TKS token balance"],
                                        ["node-info", "Query TKS blockchain node info"],
                                    ].map(([cmd, desc], i) => (
                                        <div
                                            key={cmd}
                                            className={`grid grid-cols-[140px_1fr] px-4 py-2 text-xs ${
                                                i % 2 === 0 ? "bg-muted/10" : ""
                                            }`}
                                        >
                                            <code className="font-mono font-semibold text-primary">{cmd}</code>
                                            <span className="text-muted-foreground">{desc}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Encryption & Identity */}
                            <div className="grid gap-4 sm:grid-cols-2 mb-6">
                                {[
                                    "AES-256-GCM end-to-end encryption (Double Ratchet)",
                                    "BIP-39 identity — Ed25519 + X25519 + Sr25519 + secp256k1",
                                    "Files split into 2MB chunks with Merkle tree integrity",
                                    "Identity stored at ~/.cipher/identity.json (AES-256-GCM encrypted)",
                                    "No data stored on blockchain — messages are temporary relay chunks",
                                    "TKS NameRegistry pallet for FREE @username registration",
                                    "Decentralized relay at wss://signal.tokenkickstarter.com",
                                    "Interactive real-time bidirectional chat mode",
                                ].map((feat, i) => (
                                    <div key={i} className="flex items-center gap-3 text-sm">
                                        <Zap className="h-4 w-4 text-primary shrink-0" />
                                        <span className="text-muted-foreground">{feat}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Network Endpoints */}
                            <div className="mb-6 p-4 rounded-xl bg-muted/20 border border-muted/30">
                                <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                    <Network className="h-4 w-4 text-primary" />
                                    Network Endpoints
                                </h4>
                                <div className="grid gap-2 sm:grid-cols-2 text-xs">
                                    {[
                                        { label: "TKS Blockchain RPC", value: "rpc.tokenkickstarter.com" },
                                        { label: "Signal Relay (WSS)", value: "signal.tokenkickstarter.com" },
                                        { label: "Seed Node (US)", value: "seed.tokenkickstarter.com" },
                                        { label: "Seed Node (EU)", value: "seed.tkstoken.com" },
                                        { label: "Seed Node (Asia)", value: "seed.tksscan.com" },
                                    ].map((ep) => (
                                        <div key={ep.label} className="flex items-center gap-2 p-2 rounded-lg bg-background/50">
                                            <span className="text-muted-foreground font-medium">{ep.label}:</span>
                                            <code className="font-mono text-foreground">{ep.value}</code>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Download & Quick Install */}
                            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <Download className="h-4 w-4 text-primary" />
                                    Download Cipher CLI v0.1.0
                                </h4>
                                <div className="grid gap-2 sm:grid-cols-3 text-xs mb-4">
                                    {[
                                        { platform: "macOS (Apple Silicon)", file: "cipher-cli-macos-arm64.tar.gz" },
                                        { platform: "macOS (Intel x64)", file: "cipher-cli-macos-x64.tar.gz" },
                                        { platform: "Linux (x86_64)", file: "cipher-cli-linux-x64.tar.gz" },
                                        { platform: "Linux (ARM64)", file: "cipher-cli-linux-arm64.tar.gz" },
                                        { platform: "Windows (x86_64)", file: "cipher-cli-windows-x64.zip" },
                                    ].map((dl) => (
                                        <a
                                            key={dl.file}
                                            href={`https://github.com/TokenKickstarter/Cipher-cli/releases/download/v0.1.0/${dl.file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 p-2 rounded-lg bg-background/50 hover:bg-primary/10 transition-colors"
                                        >
                                            <Download className="h-3 w-3 text-primary shrink-0" />
                                            <span className="text-muted-foreground hover:text-foreground transition-colors">{dl.platform}</span>
                                        </a>
                                    ))}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    <strong className="text-foreground">Source & Docs:</strong>{" "}
                                    <a href="https://github.com/TokenKickstarter/Cipher-cli" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                                        github.com/TokenKickstarter/Cipher-cli
                                    </a>
                                    {" "}— MIT License. Build from source with <code className="text-xs font-mono bg-muted/30 px-1.5 py-0.5 rounded">cargo build --release</code> (Rust 1.70+).
                                </p>
                            </div>
                        </motion.div>

                    </div>
                </section>

                {/* ═══ 13. INVESTOR BENEFITS & FUTURE OUTLOOK ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="investor-benefits"
                            icon={TrendingUp}
                            badge="Section 12"
                            title="Investor Benefits"
                            titleHighlight="& Future Outlook"
                            description="How TKS token holders benefit from platform growth, upcoming revenue streams, and unreleased product expansions."
                        />
                        <div className="max-w-4xl mx-auto space-y-8">

                            {/* Why Contribute */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <Coins className="h-5 w-5 text-primary" />
                                    Why Contribute to TKS
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    TokenKickstarter is not a single-product token — it is the native utility asset powering
                                    an expanding ecosystem of DeFi infrastructure tools. Every platform fee, every presale
                                    creation, every token deployment, and every upcoming product feeds value back into TKS.
                                    Early contributors gain exposure to the full product pipeline at the lowest possible
                                    entry point, before exchange listings and product launches drive organic demand.
                                </p>

                                <div className="grid gap-4 sm:grid-cols-2">
                                    {[
                                        {
                                            title: "Presale-Stage Pricing",
                                            desc: "Contributors acquire TKS from $0.01 to $0.09 across presale stages — significantly below the projected listing price. As the platform onboards projects and generates fee revenue, organic buy pressure from platform operations supports price appreciation."
                                        },
                                        {
                                            title: "Passive Reflection Rewards",
                                            desc: "TKS implements a 1% reflection fee on every transaction. As trading volume grows post-listing, holders earn proportional rewards automatically — no staking required, no gas cost, no claim transactions."
                                        },
                                        {
                                            title: "Staking Yield (8–40% APY)",
                                            desc: "TKS holders can stake tokens in dedicated pools with on-chain enforced APY tiers: 8% (30-day), 15% (90-day), 25% (180-day), and 40% (365-day). Rewards are funded from platform revenue allocation."
                                        },
                                        {
                                            title: "Platform Fee Revenue Share",
                                            desc: "Every presale creation ($20), token deployment ($20), and future premium feature generates protocol revenue. A portion of collected fees is allocated to TKS buybacks and staking reward pools, creating sustained demand."
                                        },
                                        {
                                            title: "Multi-Product Ecosystem Exposure",
                                            desc: "TKS is not tied to a single product. It powers token creation, presale management, token locking, staking, and upcoming products — each new product creates an additional demand vector for TKS."
                                        },
                                        {
                                            title: "Early Access & Governance",
                                            desc: "TKS holders will receive priority access to new product launches, beta features, and reduced platform fees. Future governance proposals will allow holders to vote on fee structures, new chain deployments, and ecosystem fund allocation."
                                        }
                                    ].map((item, i) => (
                                        <div key={i} className="p-4 rounded-xl bg-muted/30">
                                            <h4 className="font-bold text-sm mb-2 text-primary">{item.title}</h4>
                                            <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Revenue Streams & Value Accrual */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <CircleDollarSign className="h-5 w-5 text-primary" />
                                    Revenue Streams & Token Value Accrual
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    Unlike many DeFi projects that rely solely on token inflation for growth,
                                    TokenKickstarter generates real protocol revenue from day one. Each revenue
                                    stream directly or indirectly benefits TKS holders through buybacks, staking
                                    rewards, and ecosystem expansion.
                                </p>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="border-b border-border/50">
                                                <th className="text-left py-3 px-3 text-xs font-bold text-muted-foreground uppercase">Revenue Source</th>
                                                <th className="text-left py-3 px-3 text-xs font-bold text-muted-foreground uppercase">Fee</th>
                                                <th className="text-left py-3 px-3 text-xs font-bold text-muted-foreground uppercase">Status</th>
                                                <th className="text-left py-3 px-3 text-xs font-bold text-muted-foreground uppercase">TKS Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-xs text-muted-foreground">
                                            {[
                                                ["Presale Creation", "$20 USD", "Live", "Revenue → buyback pool"],
                                                ["Token Deployment", "$20 USD", "Live", "Revenue → staking rewards"],
                                                ["Fair Launch Creation", "$20 USD", "Live", "Revenue → buyback pool"],
                                                ["Token Locking", "Free (promotional)", "Live", "User acquisition → volume"],
                                                ["Staking Deposits", "Configurable %", "Live", "Fee → treasury"],
                                                ["Premium Listings", "$50–$500 USD", "Q3 2026", "Revenue → buyback + burn"],
                                                ["Promoted Presales", "$100–$1000 USD", "Q3 2026", "Revenue → ecosystem fund"],
                                                ["KYC/Audit Badges", "$200–$2000 USD", "Q4 2026", "Revenue → buyback pool"],
                                                ["Encrypted Messaging (see below)", "Freemium + Premium", "2027", "New demand vector for TKS"],
                                                ["API Access (Enterprise)", "Monthly subscription", "Q4 2026", "Revenue → staking rewards"],
                                            ].map((row, i) => (
                                                <tr key={i} className="border-b border-border/20">
                                                    {row.map((cell, j) => (
                                                        <td key={j} className={`py-2.5 px-3 ${j === 0 ? 'font-medium text-foreground' : ''}`}>
                                                            {cell}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-bold text-primary">Value Accrual Model:</span> Platform revenue is
                                        allocated across three channels: (1) <strong>40% to TKS buyback and burn</strong> — reducing
                                        circulating supply permanently, (2) <strong>35% to staking reward pool</strong> — funding
                                        APY payouts for stakers, (3) <strong>25% to operations and development</strong> — funding
                                        new product builds, security audits, and team expansion. This model ensures that every dollar
                                        of platform revenue creates sustained buy pressure on TKS while simultaneously funding growth.
                                    </p>
                                </div>
                            </motion.div>

                            {/* Upcoming Product: Encrypted Messaging */}
                            <motion.div
                                className="glass-card p-8 sm:p-10 relative overflow-hidden"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                {/* Confidential badge */}
                                <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1">
                                    <Shield className="h-3 w-3 text-yellow-500" />
                                    <span className="text-[10px] font-bold text-yellow-500 uppercase tracking-wider">Confidential</span>
                                </div>

                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <Lock className="h-5 w-5 text-primary" />
                                    Upcoming Product: End-to-End Encrypted Messaging Platform
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                                    TokenKickstarter is actively developing a <strong>privacy-first, end-to-end encrypted
                                    messaging platform</strong> designed specifically for the Web3 and crypto community.
                                    This product is currently in the confidential development phase — technical architecture
                                    details and branding are under NDA and will be announced in a dedicated reveal event.
                                </p>

                                <div className="space-y-4 mb-6">
                                    <div className="p-4 rounded-xl bg-muted/30">
                                        <h4 className="font-bold text-sm mb-2">What We Can Share</h4>
                                        <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                                            {[
                                                "Military-grade end-to-end encryption for all messages, files, and media — zero server-side plaintext storage",
                                                "Wallet-based authentication: sign in with MetaMask, WalletConnect, or any Web3 wallet — no phone number or email required",
                                                "On-chain identity verification: optional ENS/BNB name resolution and on-chain reputation scoring",
                                                "Token-gated group chats: create communities accessible only to holders of specific tokens or NFTs",
                                                "Integrated DeFi alerts: real-time price movements, presale notifications, and whale activity — all inside the chat",
                                                "Cross-platform support: web, iOS, Android, and desktop — with full message sync and self-destructing messages",
                                                "Decentralized infrastructure: messages routed through distributed nodes — no single point of failure or censorship"
                                            ].map((item, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <span className="text-primary shrink-0">•</span>
                                                    <span>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-xl bg-muted/30">
                                        <h4 className="font-bold text-sm mb-2">How This Benefits TKS Holders</h4>
                                        <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                                            {[
                                                "TKS will serve as the native utility token within the messaging platform — used for premium features, enhanced storage, and priority message delivery",
                                                "Premium subscription tiers will require TKS staking or holding, creating sustained demand independent of the launchpad business",
                                                "Token-gated features (custom bots, API access, unlimited groups) will require TKS balance thresholds",
                                                "Revenue from the messaging platform feeds into the same buyback-and-burn model — directly reducing TKS circulating supply",
                                                "Early TKS holders will receive founding member status with lifetime premium access and exclusive feature unlocks",
                                                "The messaging platform opens TKS to a significantly larger addressable market beyond DeFi — every crypto trader, community manager, and project team needs secure communication"
                                            ].map((item, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <span className="text-primary shrink-0">→</span>
                                                    <span>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5">
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        <span className="font-bold text-yellow-500">⚠ Confidentiality Notice:</span> The messaging
                                        platform is currently in active development under a separate brand identity. Product name,
                                        technical architecture specifics, and launch timeline are subject to NDA. A formal public
                                        announcement with brand reveal, beta access, and tokenomics integration details is planned
                                        for <strong>Q1 2027</strong>. TKS presale contributors will be the first to receive beta
                                        invitations and founding member status.
                                    </p>
                                </div>
                            </motion.div>

                            {/* Strategic Roadmap for Investors */}
                            <motion.div
                                className="glass-card p-8 sm:p-10"
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                            >
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                    <Rocket className="h-5 w-5 text-primary" />
                                    Strategic Growth Roadmap
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                                    Each milestone below introduces a new revenue stream or demand catalyst for TKS.
                                    The roadmap is designed so that no single quarter depends on a single event — value
                                    accrual is continuous and compounding.
                                </p>

                                <div className="space-y-4">
                                    {[
                                        {
                                            phase: "Q2 2026",
                                            title: "Foundation",
                                            items: [
                                                "TKS presale completion and DEX listing (PancakeSwap + Uniswap)",
                                                "Staking pools activated — TKS, LP, USDT, BNB pools live",
                                                "First revenue cycle: presale/token creation fees → buyback begins",
                                                "CoinGecko and CoinMarketCap listing applications submitted"
                                            ]
                                        },
                                        {
                                            phase: "Q3 2026",
                                            title: "Revenue Expansion",
                                            items: [
                                                "Premium listing directory with promoted presales and verified badges",
                                                "Multi-chain expansion to Base and Polygon — new user markets",
                                                "CEX listing outreach (Tier-2 exchanges)",
                                                "First quarterly TKS burn event from accumulated buyback pool"
                                            ]
                                        },
                                        {
                                            phase: "Q4 2026",
                                            title: "Enterprise & Compliance",
                                            items: [
                                                "Enterprise API access for institutional clients (monthly subscription model)",
                                                "Integrated KYC/audit badge system — premium revenue stream",
                                                "Governance module deployment — TKS holders vote on fee allocation",
                                                "Mobile app launch (iOS + Android) for presale management"
                                            ]
                                        },
                                        {
                                            phase: "Q1 2027",
                                            title: "Product Expansion",
                                            items: [
                                                "Encrypted messaging platform public beta — TKS utility expansion",
                                                "Founding member NFTs airdropped to early TKS presale contributors",
                                                "TKS integrated as messaging platform utility token",
                                                "Second major product announcement (details under NDA)"
                                            ]
                                        }
                                    ].map((phase, i) => (
                                        <div key={i} className="flex gap-4">
                                            <div className="flex flex-col items-center">
                                                <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                                                    {i + 1}
                                                </div>
                                                {i < 3 && <div className="w-px h-full bg-border/30 mt-2" />}
                                            </div>
                                            <div className="pb-6">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{phase.phase}</span>
                                                    <h4 className="font-bold text-sm">{phase.title}</h4>
                                                </div>
                                                <div className="space-y-1.5">
                                                    {phase.items.map((item, j) => (
                                                        <div key={j} className="flex gap-2 text-xs text-muted-foreground">
                                                            <span className="text-primary shrink-0">✓</span>
                                                            <span>{item}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </section>

                {/* ═══ 13. MARKET ANALYSIS ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="market-analysis"
                            icon={Building2}
                            badge="Section 12"
                            title="Market"
                            titleHighlight="Analysis"
                            description="How TokenKickstarter's architecture and fee model compares to existing launchpad infrastructure."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="prose prose-lg dark:prose-invert max-w-none">
                                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                    <Building2 className="h-5 w-5 text-primary" />
                                    Launchpad Market Landscape
                                </h3>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg">
                                    The EVM-compatible token launchpad market is currently dominated by three incumbents:
                                    PinkSale, DXSale, and Gempad. These platforms collectively process thousands of token
                                    launches monthly but share a common architectural limitation — they operate as monolithic
                                    contracts where all presale logic, fee collection, and token management occur within a
                                    single contract deployment. This creates systemic risk: a vulnerability in the core
                                    factory contract could theoretically compromise all active presales simultaneously.
                                </p>
                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-4">
                                    TokenKickstarter&apos;s factory isolation model addresses this by deploying each presale,
                                    token, and lock as an independent contract with its own state. The PresaleFactory at
                                    <code className="text-xs bg-muted px-1 py-0.5 rounded mx-1">0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D</code>
                                    serves only as a deployer — it creates new Presale contract instances but does not hold
                                    user funds or manage presale state. Once deployed, each presale contract operates autonomously
                                    with its own ReentrancyGuard and SafeERC20 <sup>[3][4]</sup> protections.
                                </p>

                                <h3 className="text-xl font-bold mb-4 mt-8 flex items-center gap-2">
                                    <CircleDollarSign className="h-5 w-5 text-primary" />
                                    Fee Structure Comparison
                                </h3>

                                <div className="overflow-x-auto -mx-4 sm:mx-0">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr className="border-b border-border/30">
                                                <th className="text-left py-3 px-4 font-semibold text-foreground">Feature</th>
                                                <th className="text-left py-3 px-4 font-semibold text-primary">TokenKickstarter</th>
                                                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">PinkSale</th>
                                                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">DXSale</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-muted-foreground">
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Token Creation Fee</td>
                                                <td className="py-3 px-4 text-primary font-medium">$20 flat (Chainlink-converted)</td>
                                                <td className="py-3 px-4">0.01–0.02 BNB</td>
                                                <td className="py-3 px-4">Varies by tier</td>
                                            </tr>
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Presale Raise Fee</td>
                                                <td className="py-3 px-4 text-primary font-medium">0% of raised funds</td>
                                                <td className="py-3 px-4">2–5% of raised funds</td>
                                                <td className="py-3 px-4">2–5% of raised funds</td>
                                            </tr>
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Presale Creation</td>
                                                <td className="py-3 px-4 text-primary font-medium">$150 flat</td>
                                                <td className="py-3 px-4">1–2 BNB + % of raise</td>
                                                <td className="py-3 px-4">Variable</td>
                                            </tr>
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Token Locking</td>
                                                <td className="py-3 px-4 text-primary font-medium">Free (0 fee)</td>
                                                <td className="py-3 px-4">Free</td>
                                                <td className="py-3 px-4">Free</td>
                                            </tr>
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Contract Architecture</td>
                                                <td className="py-3 px-4 text-primary font-medium">Isolated factory pattern</td>
                                                <td className="py-3 px-4">Monolithic</td>
                                                <td className="py-3 px-4">Monolithic</td>
                                            </tr>
                                            <tr className="border-b border-border/20">
                                                <td className="py-3 px-4">Cross-Chain Claims</td>
                                                <td className="py-3 px-4 text-primary font-medium">ECDSA vault (native)</td>
                                                <td className="py-3 px-4">Not supported</td>
                                                <td className="py-3 px-4">Not supported</td>
                                            </tr>
                                            <tr>
                                                <td className="py-3 px-4">Price Oracle</td>
                                                <td className="py-3 px-4 text-primary font-medium">Chainlink V3</td>
                                                <td className="py-3 px-4">None (fixed BNB)</td>
                                                <td className="py-3 px-4">None (fixed BNB)</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <p className="text-muted-foreground leading-relaxed text-base sm:text-lg mt-6">
                                    The critical differentiator is the elimination of percentage-based raise fees. A project
                                    raising $500,000 on PinkSale pays $10,000–$25,000 in platform fees. On TokenKickstarter,
                                    the same project pays a flat $150 creation fee regardless of raise amount — a 98.5–99.4%
                                    fee reduction. This fixed-fee model is enforced at the smart contract level through the
                                    ChainlinkFeeManager base contract <sup>[7]</sup>, which converts USD amounts to native
                                    currency using on-chain oracle data and cannot be overridden by platform operators.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 13. RISK DISCLOSURES ═══ */}
                <section className="py-16 md:py-20">
                    <div className="container">
                        <SectionHeading
                            id="risk-disclosures"
                            icon={Shield}
                            badge="Section 13"
                            title="Risk"
                            titleHighlight="Disclosures"
                            description="Transparency about known risks, limitations, and assumptions in the TKS ecosystem."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-6">
                                {[
                                    {
                                        title: "Smart Contract Risk",
                                        desc: "Despite using OpenZeppelin's battle-tested libraries [3] and undergoing third-party audits via SolidityScan [8], smart contracts may contain undiscovered vulnerabilities. The UUPS proxy pattern introduces upgrade risk — a malicious or compromised owner could theoretically deploy harmful implementation contracts. TKS mitigates this through Ownable2StepUpgradeable, requiring explicit acceptance of ownership transfers.",
                                    },
                                    {
                                        title: "Oracle Dependency Risk",
                                        desc: "TKS's fee conversion and presale pricing depend on Chainlink price feed availability [7]. If Chainlink oracles experience downtime or return stale data beyond the 1-hour threshold, the platform falls back to cached pricing which may not reflect current market conditions. Prolonged oracle failure could temporarily prevent new deployments and contributions.",
                                    },
                                    {
                                        title: "Regulatory Risk",
                                        desc: "The legal classification of utility tokens, presale mechanisms, and staking rewards varies by jurisdiction and is subject to change. TKS tokens may be classified as securities in certain jurisdictions. Users are solely responsible for compliance with their local regulatory requirements. TokenKickstarter does not provide legal, financial, or investment advice.",
                                    },
                                    {
                                        title: "Market Risk",
                                        desc: "TKS token value is subject to cryptocurrency market volatility. There is no guarantee of token price appreciation. Staking APY rates are denominated in TKS tokens and do not guarantee USD-equivalent returns. Past performance of staking rewards does not indicate future results.",
                                    },
                                    {
                                        title: "Cross-Chain Claim Risk",
                                        desc: "The TKSClaimVault's ECDSA signature verification [5] relies on a centralized signer service to authorize cross-chain claims. While the cryptographic verification occurs on-chain, the signature generation is an off-chain process that represents a single point of failure. If the signer service is compromised or unavailable, cross-chain claims may be delayed or require manual intervention.",
                                    },
                                    {
                                        title: "Liquidity Risk",
                                        desc: "Token locking via TokenLockerUpgradeable [9] removes liquidity from circulation for the specified duration. While locks protect against rug pulls, they also mean locked tokens cannot be accessed during emergency market conditions. The 51% minimum liquidity allocation on presale finalization is enforced at the contract level and cannot be reduced post-deployment.",
                                    },
                                ].map((risk, i) => (
                                    <div key={i} className="p-5 rounded-xl bg-muted/30">
                                        <h4 className="font-bold mb-2 flex items-center gap-2">
                                            <Shield className="h-4 w-4 text-amber-500" />
                                            {risk.title}
                                        </h4>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{risk.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ═══ 14. REFERENCES ═══ */}
                <section className="py-16 md:py-20 bg-muted/20">
                    <div className="container">
                        <SectionHeading
                            id="references"
                            icon={BookOpen}
                            badge="Section 14"
                            title="Technical"
                            titleHighlight="References"
                            description="Standards, protocols, and technical documentation referenced in this whitepaper."
                        />
                        <motion.div
                            className="glass-card p-8 sm:p-10 max-w-4xl mx-auto"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                        >
                            <div className="space-y-3">
                                {[
                                    { id: 1, text: "S. Nakamoto, \"Bitcoin: A Peer-to-Peer Electronic Cash System,\" 2008.", url: "https://bitcoin.org/bitcoin.pdf" },
                                    { id: 2, text: "V. Buterin, \"Ethereum: A Next-Generation Smart Contract and Decentralized Application Platform,\" 2014.", url: "https://ethereum.org/en/whitepaper/" },
                                    { id: 3, text: "OpenZeppelin, \"OpenZeppelin Contracts v5.x — Security-audited Solidity framework.\"", url: "https://docs.openzeppelin.com/contracts/5.x/" },
                                    { id: 4, text: "EIP-20: Token Standard — Fabian Vogelsteller, Vitalik Buterin, 2015.", url: "https://eips.ethereum.org/EIPS/eip-20" },
                                    { id: 5, text: "EIP-2612: Permit Extension for EIP-20 Signed Approvals — Martin Lundfall, 2020.", url: "https://eips.ethereum.org/EIPS/eip-2612" },
                                    { id: 6, text: "EIP-1967: Standard Proxy Storage Slots — Santiago Palladino, 2019.", url: "https://eips.ethereum.org/EIPS/eip-1967" },
                                    { id: 7, text: "Chainlink, \"Using Data Feeds — Chainlink Documentation.\"", url: "https://docs.chain.link/data-feeds" },
                                    { id: 8, text: "SolidityScan, \"TokenKickstarter Smart Contract Security Audit Report.\"", url: "https://solidityscan.com/published-report/block/67a2e7d2-1bdc-472a-beb6-6b4e7e1e0638" },
                                    { id: 9, text: "OpenZeppelin, \"UUPS Proxies — Universal Upgradeable Proxy Standard.\"", url: "https://docs.openzeppelin.com/contracts/5.x/api/proxy#UUPSUpgradeable" },
                                    { id: 10, text: "EIP-1822: Universal Upgradeable Proxy Standard (UUPS) — Gabriel Barros, Patrick Gallagher, 2019.", url: "https://eips.ethereum.org/EIPS/eip-1822" },
                                    { id: 11, text: "OpenZeppelin, \"ReentrancyGuard — Prevents reentrant calls to a function.\"", url: "https://docs.openzeppelin.com/contracts/5.x/api/utils#ReentrancyGuard" },
                                    { id: 12, text: "Chainlink, \"AggregatorV3Interface — Price Feed Interface Reference.\"", url: "https://docs.chain.link/data-feeds/api-reference#aggregatorv3interface" },
                                ].map((ref) => (
                                    <div key={ref.id} className="flex gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-all group">
                                        <span className="text-primary font-mono text-sm font-bold shrink-0 w-8 text-right">[{ref.id}]</span>
                                        <div className="min-w-0">
                                            <p className="text-sm text-muted-foreground leading-relaxed">{ref.text}</p>
                                            <a
                                                href={ref.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-primary/70 hover:text-primary transition-colors break-all flex items-center gap-1 mt-1"
                                            >
                                                {ref.url}
                                                <ExternalLink className="h-3 w-3 shrink-0" />
                                            </a>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8 pt-6 border-t border-border/30">
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    <strong className="text-foreground">Disclaimer:</strong> This whitepaper is provided for informational purposes only and does not constitute
                                    financial, legal, or investment advice. The information contained herein is subject to change without
                                    notice. Cryptocurrency investments carry inherent risk, including the potential loss of principal.
                                    Readers should conduct their own due diligence and consult qualified professionals before making
                                    any investment decisions. TokenKickstarter makes no warranties or representations regarding the
                                    accuracy, completeness, or reliability of this document.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                </article>
            </main>
            <Footer />
        </div>
    )
}
